import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";  // ✅ Import useAuth
import LiveMonitorTab_dynamic from "./LiveMonitorTab_dynamic";
import LiveMonitorTab_webrtc from "./LiveMonitorTab_webrtc";  // ✅ NEW: WebRTC monitoring
import { buildApiUrl } from "./config";  // ✅ Import config
import StudentPage from "./StudentPage";  // Import StudentPage
import ChatWindow from "./ChatWindow";  // Import ChatWindow for messaging
import ResourcesTab from "./components/ResourcesTab";  // ✅ Import ResourcesTab
import ReportsTab from "./ReportsTab";  // ✅ Import ReportsTab
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

// Enhanced Live Monitor Component for FormPage integration
const EnhancedLiveMonitor = ({ formId, students }) => {
  const { teacherEmail, role } = useAuth();  // ✅ Get current authentication
  const [connectedStudents, setConnectedStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [websocket, setWebsocket] = useState(null);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [screenData, setScreenData] = useState({});
  const [teacherInfo, setTeacherInfo] = useState(null);
  
  const server = buildApiUrl("");

  // Auto-setup teacher info from existing auth
  useEffect(() => {
    // Use a default teacher setup since we're already in an authenticated teacher context
    const defaultTeacherEmail = localStorage.getItem('teacherEmail') || 'teacher@school.edu';
    // Use the full email as teacher ID
    const teacherId = defaultTeacherEmail;
    const teacherName = defaultTeacherEmail.split('@')[0].replace(/[._]/g, ' ');
    
    setTeacherInfo({
      teacher_id: teacherId,
      teacher_name: teacherName.charAt(0).toUpperCase() + teacherName.slice(1),
      teacher_email: defaultTeacherEmail,
      subject: formId // Use form as subject for now
    });
    
    // Automatically connect to monitoring
    connectToMonitoring();
    loadTeacherSession();
  }, [formId]);

  const connectToMonitoring = () => {
    const defaultTeacherEmail = localStorage.getItem('teacherEmail') || 'teacher@school.edu';
    // Use the full email as teacher ID for WebSocket connection
    const teacherId = defaultTeacherEmail;
    const wsUrl = `${server.replace('http', 'ws')}/ws/teacher-monitor/${encodeURIComponent(teacherId)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('✅ Teacher monitoring connected for', teacherEmail);
      setConnectionStatus('connected');
      setWebsocket(ws);
      ws.send(JSON.stringify({ type: 'get_students' }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'student_list':
            // Filter students by form ID to show only relevant students
            const formStudents = (data.students || []).filter(student => 
              student.id.includes(formId) || student.form === formId
            );
            setConnectedStudents(formStudents);
            console.log(`📋 Form ${formId} students:`, formStudents.length);
            break;
            
          case 'student_connected':
            console.log('👨‍🎓 Student connected:', data.student_name);
            ws.send(JSON.stringify({ type: 'get_students' }));
            break;
            
          case 'student_disconnected':
            console.log('👋 Student disconnected:', data.student_id);
            setScreenData(prev => {
              const updated = { ...prev };
              delete updated[data.student_id];
              return updated;
            });
            ws.send(JSON.stringify({ type: 'get_students' }));
            break;
            
          case 'screen_update':
            // Only update if student belongs to current form
            if (data.student_id.includes(formId)) {
              setScreenData(prev => ({
                ...prev,
                [data.student_id]: {
                  data: data.data,
                  timestamp: data.timestamp
                }
              }));
            }
            break;
        }
      } catch (error) {
        console.error('WebSocket message error:', error);
      }
    };

    ws.onclose = () => {
      console.log('👋 Teacher monitoring disconnected');
      setConnectionStatus('disconnected');
      setWebsocket(null);
      
      // Try to reconnect after 5 seconds
      setTimeout(() => {
        connectToMonitoring();
      }, 5000);
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      setError('Connection failed. Retrying...');
      setConnectionStatus('error');
    };
  };

  const loadTeacherSession = async () => {
    const defaultTeacherEmail = localStorage.getItem('teacherEmail') || 'teacher@school.edu';
    // Use the full email as teacher ID
    const teacherId = defaultTeacherEmail;
    
    try {
      const response = await fetch(`${server}/api/teacher/session/${encodeURIComponent(teacherId)}`);
      const data = await response.json();
      
      // Filter for current form
      const formStudents = (data.students || []).filter(student => 
        student.id.includes(formId) || student.form === formId
      );
      setConnectedStudents(formStudents);
    } catch (error) {
      console.error('Failed to load teacher session:', error);
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return '#10B981';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div>
          <h3 style={{ margin: '0 0 5px 0', color: '#1e293b' }}>
            👨‍🏫 {teacherInfo?.teacher_name || teacherInfo?.teacher_email || 'Teacher'}
          </h3>
          <p style={{ margin: '0', color: '#64748b' }}>
            Form {formId} Live Monitor • {teacherInfo?.teacher_email || 'teacher@school.edu'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: getStatusColor() }}></div>
            <span style={{ fontWeight: '500' }}>{connectionStatus}</span>
          </div>
          <div style={{ background: '#f1f5f9', padding: '8px 12px', borderRadius: '6px', fontSize: '14px', color: '#475569' }}>
            🔗 Auto-connected
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '15px', marginBottom: '20px', borderRadius: '8px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Student Connection Instructions */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>📱 Student Connection Instructions</h4>
        <p style={{ margin: '0 0 10px 0', color: '#1e40af' }}>
          Share this link with your Form {formId} students:
        </p>
        <div style={{ background: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd', fontFamily: 'monospace', fontSize: '14px', color: '#1e40af', fontWeight: '600' }}>
          {window.location.origin}/student-connection/{formId}
        </div>
        <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#3b82f6' }}>
          💡 Students will automatically connect to your monitoring session when they use this link.
        </p>
      </div>

      {/* Monitor Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '20px', minHeight: '600px' }}>
        {/* Students Panel */}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: '0', color: '#1e293b' }}>📋 Form {formId} Students ({connectedStudents.length})</h4>
          </div>
          
          <div style={{ padding: '10px', maxHeight: '500px', overflowY: 'auto' }}>
            {connectedStudents.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                <p style={{ margin: '10px 0' }}>👥 No students connected</p>
                <p style={{ margin: '10px 0', fontSize: '14px' }}>Students from Form {formId} can connect using the link above</p>
                {connectionStatus === 'connected' && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#f0fdf4', borderRadius: '6px' }}>
                    <p style={{ margin: '0', fontSize: '12px', color: '#166534' }}>
                      ✅ Monitoring system is active and ready for connections
                    </p>
                  </div>
                )}
              </div>
            ) : (
              connectedStudents.map(student => (
                <div 
                  key={student.id}
                  onClick={() => setSelectedStudent(student)}
                  style={{ 
                    border: selectedStudent?.id === student.id ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                    borderRadius: '12px', 
                    padding: '15px', 
                    marginBottom: '10px', 
                    cursor: 'pointer',
                    background: selectedStudent?.id === student.id ? '#eff6ff' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <h5 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>{student.name}</h5>
                  <p style={{ margin: '3px 0', color: '#64748b', fontSize: '12px' }}>ID: {student.id}</p>
                  <p style={{ margin: '3px 0', color: '#64748b', fontSize: '12px' }}>Connected: {formatTimestamp(student.connected_at)}</p>
                  <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: student.status === 'online' ? '#059669' : '#dc2626', fontSize: '12px', fontWeight: '500' }}>
                      {student.status === 'online' ? '🟢' : '🔴'} {student.status}
                    </span>
                    {screenData[student.id] && (
                      <span style={{ background: '#10b981', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                        📱 Live
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Monitor Panel */}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: '0', color: '#1e293b' }}>
              📺 Student Screen Monitor 
              {selectedStudent && <span style={{ color: '#3b82f6', fontSize: '14px' }}> - {selectedStudent.name}</span>}
            </h4>
          </div>
          
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            {!selectedStudent ? (
              <div style={{ textAlign: 'center', color: '#64748b' }}>
                <p style={{ fontSize: '18px' }}>👆 Select a student to view their screen</p>
                <p style={{ fontSize: '14px', marginTop: '10px' }}>
                  Students will appear here when they connect using the shared link
                </p>
              </div>
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {screenData[selectedStudent.id] ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', maxWidth: '100%', maxHeight: '100%' }}>
                    <img 
                      src={screenData[selectedStudent.id].data}
                      alt={`${selectedStudent.name}'s screen`}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 'calc(100% - 60px)', 
                        borderRadius: '8px', 
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        objectFit: 'contain',
                        background: '#000'
                      }}
                    />
                    <div style={{ background: '#f8fafc', padding: '12px 20px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                      <p style={{ margin: '0', color: '#475569', fontSize: '14px', fontWeight: '500' }}>
                        Last Update: {formatTimestamp(screenData[selectedStudent.id].timestamp)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#64748b' }}>
                    <p style={{ fontSize: '16px' }}>📱 Waiting for screen data from {selectedStudent.name}</p>
                    <p style={{ fontSize: '14px', marginTop: '10px' }}>
                      The student needs to allow screen sharing and start the connection
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div style={{ marginTop: '20px', padding: '15px 30px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', gap: '30px', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
          <p style={{ margin: '0' }}>🔗 Status: {connectionStatus}</p>
          <p style={{ margin: '0' }}>👥 Students: {connectedStudents.length}</p>
          <p style={{ margin: '0' }}>📱 Live Screens: {Object.keys(screenData).length}</p>
          <p style={{ margin: '0' }}>📚 Form: {formId}</p>
          <p style={{ margin: '0' }}>👨‍🏫 Teacher: {teacherEmail}</p>
        </div>
      </div>
    </div>
  );
};

// Local Monitor Tab Component - Opens local monitoring server
const LocalMonitorTab = ({ formId, students }) => {
  const [monitorWindow, setMonitorWindow] = useState(null);
  const [isServerRunning, setIsServerRunning] = useState(false);
  
  // Check if local monitoring server is running
  useEffect(() => {
    const checkServer = async () => {
      try {
        // Check if the form-specific page exists
        const response = await fetch(`http://localhost:9000/${formId}`, { mode: 'no-cors' });
        setIsServerRunning(true);
      } catch (error) {
        setIsServerRunning(false);
      }
    };
    
    checkServer();
    // Check every 5 seconds
    const interval = setInterval(checkServer, 5000);
    return () => clearInterval(interval);
  }, []);

  const openLocalMonitor = () => {
    const url = `http://localhost:9000/${formId}`;
    const newWindow = window.open(
      url,
      `LocalMonitor_${formId}`,
      'width=1400,height=900,scrollbars=yes,resizable=yes,toolbar=no,menubar=no'
    );
    setMonitorWindow(newWindow);
    
    // Focus the new window
    if (newWindow) {
      newWindow.focus();
    }
  };

  const closeMonitorWindow = () => {
    if (monitorWindow && !monitorWindow.closed) {
      monitorWindow.close();
      setMonitorWindow(null);
    }
  };

  return (
    <div style={{ 
      padding: '30px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '20px',
      color: 'white',
      minHeight: '500px'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          marginBottom: '10px',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          🖥️ Local Live Monitor
        </h2>
        <p style={{ 
          fontSize: '16px', 
          opacity: '0.9',
          marginBottom: '0'
        }}>
          Form {formId} • Real-time Student Screen Monitoring
        </p>
      </div>

      {/* Server Status */}
      <div style={{
        background: 'rgba(255,255,255,0.1)',
        padding: '20px',
        borderRadius: '15px',
        marginBottom: '30px',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: isServerRunning ? '#10b981' : '#ef4444',
            boxShadow: isServerRunning 
              ? '0 0 20px rgba(16, 185, 129, 0.6)' 
              : '0 0 20px rgba(239, 68, 68, 0.6)'
          }} />
          <span style={{ fontSize: '16px', fontWeight: '600' }}>
            Server Status: {isServerRunning ? 'Running' : 'Not Running'}
          </span>
        </div>
        
        {!isServerRunning && (
          <div style={{ 
            marginTop: '15px', 
            padding: '15px', 
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '10px',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#fecaca' }}>
              ⚠️ Local monitoring server is not running. Please start the monitoring server:
            </p>
            <code style={{ 
              display: 'block', 
              marginTop: '10px', 
              padding: '8px', 
              background: 'rgba(0,0,0,0.3)', 
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'monospace'
            }}>
              python simple_student_monitor.py
            </code>
          </div>
        )}
      </div>

      {/* Monitor Controls */}
      <div style={{
        display: 'flex',
        gap: '20px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={openLocalMonitor}
          disabled={!isServerRunning}
          style={{
            padding: '15px 30px',
            fontSize: '16px',
            fontWeight: '600',
            background: isServerRunning 
              ? 'linear-gradient(45deg, #10b981, #059669)' 
              : 'rgba(156, 163, 175, 0.5)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: isServerRunning ? 'pointer' : 'not-allowed',
            boxShadow: isServerRunning 
              ? '0 4px 15px rgba(16, 185, 129, 0.4)' 
              : 'none',
            transition: 'all 0.3s ease',
            transform: isServerRunning ? 'translateY(0)' : 'translateY(0)',
            opacity: isServerRunning ? 1 : 0.6
          }}
          onMouseEnter={(e) => {
            if (isServerRunning) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (isServerRunning) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
            }
          }}
        >
          🚀 Open Form {formId} Monitor
        </button>

        {monitorWindow && !monitorWindow.closed && (
          <button
            onClick={closeMonitorWindow}
            style={{
              padding: '15px 30px',
              fontSize: '16px',
              fontWeight: '600',
              background: 'linear-gradient(45deg, #ef4444, #dc2626)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.4)';
            }}
          >
            ❌ Close Monitor
          </button>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        marginTop: '40px',
        background: 'rgba(255,255,255,0.1)',
        padding: '25px',
        borderRadius: '15px',
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          marginBottom: '15px',
          color: '#f1f5f9'
        }}>
          📋 How to Use Form-Specific Monitor
        </h3>
        <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#e2e8f0' }}>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>1.</strong> Start the monitoring server by running <code style={{ 
              background: 'rgba(0,0,0,0.3)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}>python simple_student_monitor.py</code>
          </p>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>2.</strong> Click "Open Form {formId} Monitor" to launch the form-specific dashboard
          </p>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>3.</strong> Only students from Form {formId} will be displayed in the monitoring view
          </p>
          <p style={{ margin: '0' }}>
            <strong>4.</strong> Access URL: <code style={{ 
              background: 'rgba(0,0,0,0.3)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}>localhost:9000/{formId}</code>
          </p>
        </div>
      </div>

      {/* Features */}
      <div style={{
        marginTop: '30px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px'
      }}>
        {[
          { icon: '👥', title: 'Live Student List', desc: 'See connected students in real-time' },
          { icon: '📺', title: 'Screen Viewing', desc: 'View student screens remotely' },
          { icon: '🎮', title: 'Remote Control', desc: 'Control student computers' },
          { icon: '📚', title: 'Form Organization', desc: 'Students grouped by forms/classes' }
        ].map((feature, index) => (
          <div key={index} style={{
            background: 'rgba(255,255,255,0.1)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            backdropFilter: 'blur(5px)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{feature.icon}</div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{feature.title}</div>
            <div style={{ fontSize: '12px', opacity: '0.8' }}>{feature.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const menuItems = [
  { key: "Home", label: "📊 Class Overview" },
  { key: "PupilsDetails", label: "👥 Pupils Details" },
  { key: "Chat", label: "💬 Chat" },
  { key: "Resources", label: "📚 Resources" },
  { key: "Assignments", label: "📝 Assignments" },
  { key: "Attendance", label: "✅ Attendance" },
  { key: "Timetable", label: "🕐 Timetable" },
  { key: "Rewards", label: "🏆 Rewards" },
  { key: "Monitor", label: "🖥️ Monitor" },
  { key: "Reports", label: "📈 Reports" },
];

const FormPage = () => {
  const { formId } = useParams();
  const { teacherEmail } = useAuth();
  const navigate = useNavigate();
  const [formHomeworks, setFormHomeworks] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [students, setStudents] = useState([]);
  const [viewingHwId, setViewingHwId] = useState(null);
  const [activeTab, setActiveTab] = useState("Home");
  const [connectedStudents, setConnectedStudents] = useState([]);
  const [screenPreviews, setScreenPreviews] = useState({});
  const [remoteControlSessions, setRemoteControlSessions] = useState({});
  const [fullscreenStudent, setFullscreenStudent] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formUnreadCount, setFormUnreadCount] = useState(0); // Unread count for this specific form

  const server = buildApiUrl("");  // ✅ Use config instead of localhost
  
  // Load unread count for this specific form
  useEffect(() => {
    const loadFormUnreadCount = async () => {
      try {
        const email = teacherEmail || localStorage.getItem('teacherEmail');
        if (!email) return;

        const response = await fetch(buildApiUrl(`/api/student-chat/teacher-unread/${email}`));
        const data = await response.json();
        
        if (data?.unread_by_form && data.unread_by_form[formId]) {
          setFormUnreadCount(data.unread_by_form[formId]);
        } else {
          setFormUnreadCount(0);
        }
      } catch (error) {
        console.error("Failed to load form unread count:", error);
      }
    };

    if (formId) {
      loadFormUnreadCount();
      // Refresh every 10 seconds
      const interval = setInterval(loadFormUnreadCount, 10000);
      return () => clearInterval(interval);
    }
  }, [formId, teacherEmail]);

  // Function to generate avatar color based on name
  // (Moved to HomeTab)

  useEffect(() => {
    if (formId) {
      fetch(`${server}/view-homework/${formId}`)
        .then(res => res.json())
        .then(res => setFormHomeworks(res.homeworks || []))
        .catch(err => console.error("Failed to load homework:", err));

      // Load students without photo data
      fetch(`${server}/synced-students`)
        .then(res => res.json())
        .then(res => {
          const seen = new Set();
          const filtered = res.students.filter(s => s.form === formId && !seen.has(s.school_id) && seen.add(s.school_id));
          setStudents(filtered);
        })
        .catch(err => console.error("Failed to load students:", err));

      fetch(`${server}/data/homework_submissions.json`)
        .then(res => res.json())
        .then(res => setSubmissions(res || {}))
        .catch(err => console.error("Failed to load submissions:", err));
    }
  }, [formId]);

  useEffect(() => {
    const fetchConnectedStudents = async () => {
      try {
        const res = await fetch(`${server}/active-students`);
        const data = await res.json();
        setConnectedStudents(data);
      } catch (err) {
        console.error("Failed to fetch connected students", err);
      }
    };

    fetchConnectedStudents();
    const interval = setInterval(fetchConnectedStudents, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load reading age data from JSON and merge with students
  useEffect(() => {
    if (students.length > 0) {
      fetch(`${server}/data/reading_age.json`)
        .then(res => {
          if (!res.ok) {
            console.error(`Failed to fetch reading age: ${res.status} ${res.statusText}`);
            return Promise.reject(new Error(`HTTP ${res.status}`));
          }
          return res.json();
        })
        .then(readingAgeData => {
          // Create a map of student name to latest RA
          const raMap = {};
          readingAgeData.forEach(entry => {
            if (entry.tests && entry.tests.length > 0) {
              // Find the first test with a valid RA (not "-")
              const validTest = entry.tests.find(test => test.RA !== '-');
              if (validTest) {
                raMap[entry.student] = validTest.RA;
              }
            }
          });

          // Update students with reading age data
          const updatedStudents = students.map(student => ({
            ...student,
            reading_age: raMap[student.full_name] || (student.reading_age || "N/A")
          }));
          setStudents(updatedStudents);
        })
        .catch(err => console.error("Failed to load reading age data:", err));
    }
  }, [students.length]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };

  const getSubmissionStatus = (studentId, homeworkId) => {
    const hwRecord = submissions[studentId]?.[homeworkId];
    if (hwRecord) {
      return {
        submitted: true,
        date: hwRecord.submitted_at
      };
    }
    return { submitted: false };
  };

  const myStudents = students;

  // Add a delete handler
  const handleDeleteHomework = async (hwId) => {
    if (!window.confirm("Are you sure you want to delete this assignment?")) return;
    try {
      // Use the correct backend endpoint for deletion
      const res = await fetch(`${server}/homework-by-id/${hwId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setFormHomeworks((prev) => prev.filter(hw => hw.id !== hwId));
      } else {
        alert("Failed to delete homework.");
      }
    } catch (err) {
      alert("Error deleting homework.");
      console.error(err);
    }
  };

  // Show StudentPage if a student is selected
  if (selectedStudentId) {
    return (
      <div>
        <button
          onClick={() => setSelectedStudentId(null)}
          className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          ← Back to Form {formId}
        </button>
        <StudentPage studentId={selectedStudentId} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile Hamburger Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {sidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
        />
      )}

      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:static w-64 bg-white border-r border-gray-200 flex flex-col py-8 px-4 shadow-md z-40 h-full transition-transform duration-300 ease-in-out`}>
        <div className="mb-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium mb-4 group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to My Classes
          </button>
          <h1 className="text-2xl font-bold text-blue-700">Form {formId}</h1>
          <p className="text-xs text-gray-400 mt-1">Classroom Dashboard</p>
        </div>
        <nav className="flex-1">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.key}>
                <button
                  className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors relative
                    ${activeTab === item.key
                      ? "bg-blue-600 text-white font-semibold shadow"
                      : "hover:bg-blue-100 text-gray-700"}`}
                  onClick={() => {
                    setActiveTab(item.key);
                    setSidebarOpen(false); // Close sidebar on mobile after selection
                  }}
                >
                  <span>{item.label}</span>
                  {/* Show badge on Chat tab if there are unread messages */}
                  {item.key === "Chat" && formUnreadCount > 0 && (
                    <span className="ml-2 bg-red-500 rounded-full w-2 h-2"></span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mt-10 text-xs text-gray-400">
          <span>Powered by Tailwind CSS</span>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="overflow-y-auto max-h-[calc(100vh-4rem)] pr-2">
          {activeTab === "Home" && (
            <DashboardTab students={students} formId={formId} />
          )}
          {activeTab === "PupilsDetails" && (
            <PupilsDetailsTab students={students} formId={formId} />
          )}
          {activeTab === "Chat" && (
            <HomeTab
              students={students}
              formHomeworks={formHomeworks}
              submissions={submissions}
              onStudentClick={setSelectedStudentId}
            />
          )}
          {activeTab === "Resources" && <ResourcesTab formId={formId} />}
          {activeTab === "Assignments" && (
            formHomeworks.length === 0 ? (
              <div className="text-gray-400">No assignments found for this form.</div>
            ) : (
              formHomeworks.map((hw, index) => (
                <div key={index} className="bg-white rounded-lg shadow p-6 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{hw.title}</span>
                    <span className="text-xs text-gray-400">{hw.subject}</span>
                  </div>
                  <div className="text-sm mb-2">
                    Due: <span className="font-medium">{hw.dueDate || "Not specified"}</span>
                  </div>
                  <div className="text-xs mb-2">
                    Assigned by: <span className="font-medium">{hw.createdBy || "Unknown"}</span>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <button
                      className="border border-blue-600 text-blue-600 rounded px-3 py-1 text-sm hover:bg-blue-50 transition"
                      onClick={() => setViewingHwId(viewingHwId === hw.id ? null : hw.id)}
                    >
                      {viewingHwId === hw.id ? "Hide Submissions" : "View Submissions"}
                    </button>
                    <button
                      className="border border-red-600 text-red-600 rounded px-3 py-1 text-sm hover:bg-red-50 transition"
                      onClick={() => handleDeleteHomework(hw.id)}
                    >
                      Delete
                    </button>
                  </div>
                  {viewingHwId === hw.id && (
                    <div className="mt-4">
                      <div className="font-semibold mb-2">Student Submissions:</div>
                      {myStudents.length === 0 ? (
                        <div className="text-gray-400">Loading...</div>
                      ) : (
                        <ul className="divide-y">
                          {myStudents.map((student, idx) => {
                            const status = getSubmissionStatus(student.school_id, hw.id);
                            return (
                              <li key={idx} className="flex items-center justify-between py-2">
                                <span>
                                  <span className="font-medium">{student.full_name}</span>
                                  <span className="ml-2 text-xs text-gray-400">ID: {student.school_id}</span>
                                </span>
                                <span className={status.submitted ? "text-green-600" : "text-red-500"}>
                                  {status.submitted
                                    ? status.score !== undefined
                                      ? `Score: ${status.score}/5`
                                      : "Submitted"
                                    : "❌ Not Submitted"}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))
            )
          )}
          {activeTab === "Attendance" && <AttendanceTab formId={formId} students={students} />}
          {activeTab === "Timetable" && <TimetableTab formId={formId} students={students} />}
          {activeTab === "Rewards" && <RewardsTab formId={formId} students={students} teacherEmail={teacherEmail} />}
          {activeTab === "Monitor" && (
            <LiveMonitorTab_webrtc 
              teacherId={localStorage.getItem('teacherEmail') || 'teacher@school.edu'}
              formId={formId}
            />
          )}
          {activeTab === "Reports" && (
            <ReportsTab students={students} formId={formId} />
          )}
        </div>
      </main>
    </div>
  );
};

// Dashboard Tab with Charts
const DashboardTab = ({ students, formId }) => {
  const [bmiData, setBmiData] = useState([]);
  const [loadingBmi, setLoadingBmi] = useState(true);

  // Fetch BMI data for all students
  useEffect(() => {
    const fetchBmiData = async () => {
      setLoadingBmi(true);
      try {
        const bmiPromises = students.map(async (student) => {
          try {
            const response = await fetch(buildApiUrl(`/api/medical/students/${student.school_id}/bmi`));
            const data = await response.json();
            
            if (data.measurements && data.measurements.length > 0) {
              // Get the latest measurement
              const latest = data.measurements[data.measurements.length - 1];
              return {
                name: student.full_name.split(' ')[0], // First name
                fullName: student.full_name,
                height: latest.height || 0,
                weight: latest.weight || 0,
                school_id: student.school_id
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching BMI for ${student.school_id}:`, err);
            return null;
          }
        });

        const results = await Promise.all(bmiPromises);
        const validData = results.filter(data => data !== null && data.height > 0);
        // Sort by height (smallest to biggest)
        validData.sort((a, b) => a.height - b.height);
        setBmiData(validData);
      } catch (error) {
        console.error('Error loading BMI data:', error);
      } finally {
        setLoadingBmi(false);
      }
    };

    if (students.length > 0) {
      fetchBmiData();
    }
  }, [students]);

  // Calculate age with years and months from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    
    if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
      years--;
      months += 12;
    }
    
    if (today.getDate() < birthDate.getDate()) {
      months--;
      if (months < 0) {
        months += 12;
        years--;
      }
    }
    
    // Return age with both display format and sortable total months
    return {
      display: parseFloat(`${years}.${months.toString().padStart(2, '0')}`),
      totalMonths: years * 12 + months
    };
  };

  // Prepare age data with student names
  const ageChartData = students
    .map(student => {
      const ageData = calculateAge(student.dob);
      return {
        name: student.full_name,
        age: ageData ? ageData.display : 0,
        totalMonths: ageData ? ageData.totalMonths : 0
      };
    })
    .filter(item => item.age > 0)
    .sort((a, b) => b.totalMonths - a.totalMonths); // Sort by total months for correct ordering

  // Prepare reading age data with student names
  const readingAgeChartData = students
    .map(student => ({
      name: student.full_name,
      readingAge: student.reading_age && student.reading_age !== 'N/A' ? parseFloat(student.reading_age) || 0 : 0
    }))
    .filter(item => item.readingAge > 0)
    .sort((a, b) => b.readingAge - a.readingAge); // Sort by reading age descending

  // Prepare nationality data
  const nationalityData = {};
  students.forEach(student => {
    const nationality = student.nationality || 'Unknown';
    nationalityData[nationality] = (nationalityData[nationality] || 0) + 1;
  });

  const nationalityChartData = Object.entries(nationalityData)
    .sort(([, a], [, b]) => b - a)
    .map(([nationality, count]) => ({
      nationality: nationality,
      count: count
    }));

  // Prepare gender data
  const genderData = {
    Male: students.filter(s => s.gender === 'M').length,
    Female: students.filter(s => s.gender === 'F').length
  };

  const genderChartData = [
    { name: 'Male', value: genderData.Male, color: '#3b82f6' },
    { name: 'Female', value: genderData.Female, color: '#ec4899' }
  ];

  // Prepare academic house data
  const academicHouseData = {};
  students.forEach(student => {
    const house = student.academic_house || 'Unknown';
    academicHouseData[house] = (academicHouseData[house] || 0) + 1;
  });

  const academicHouseChartData = Object.entries(academicHouseData).map(([name, value]) => ({
    name: name,
    value: value
  }));

  const HOUSE_COLORS = {
    'Amna': '#10b981',       // Green
    'Jassim': '#8b5cf6',     // Purple
    'Shepperd': '#3b82f6',   // Blue
    'Powys': '#eab308',      // Yellow
    'Irons': '#ef4444',      // Red
    'Khalifa': '#06b6d4',    // Light Blue
    'Unknown': '#9ca3af'     // Gray
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Form {formId} Dashboard</h2>
        <p className="text-gray-600">Overview of {students.length} students</p>
      </div>

      {/* Gender and Nationality Donut Charts Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Donut Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={genderChartData}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={120}
                paddingAngle={2}
                dataKey="value"
                label={false}
              >
                <Cell fill="#3b82f6" />
                <Cell fill="#ec4899" />
              </Pie>
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry) => {
                  const data = entry.payload;
                  const total = genderChartData.reduce((sum, item) => sum + item.value, 0);
                  return `${value}: ${data.value} (${((data.value / total) * 100).toFixed(1)}%)`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Nationality Horizontal Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Nationality Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={nationalityChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="nationality" type="category" width={100} />
              <Tooltip formatter={(value) => `${value} students`} />
              <Legend />
              <Bar dataKey="count" name="Number of Students" label={{ position: 'right', fontSize: 12, fontWeight: 'bold' }}>
                {nationalityChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Academic House Circle Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic House Distribution</h3>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={academicHouseChartData}
              cx="60%"
              cy="50%"
              innerRadius={80}
              outerRadius={120}
              paddingAngle={2}
              dataKey="value"
              label={false}
            >
              {academicHouseChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={HOUSE_COLORS[entry.name] || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              verticalAlign="middle"
              align="left"
              layout="vertical"
              wrapperStyle={{ paddingLeft: '20px' }}
              formatter={(value, entry) => {
                const data = entry.payload;
                const total = academicHouseChartData.reduce((sum, item) => sum + item.value, 0);
                return `${value}: ${data.value} (${((data.value / total) * 100).toFixed(1)}%)`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Reading Age Column Chart */}
      {readingAgeChartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📖 Reading Age Distribution</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={readingAgeChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={120}
                interval={0}
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Reading Age', angle: -90, position: 'insideLeft' }}
                domain={[0, 'auto']}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'readingAge') {
                    return [value.toFixed(2), 'Reading Age'];
                  }
                  return value;
                }}
              />
              <Legend />
              <Bar dataKey="readingAge" fill="#10b981" name="Reading Age" label={{ position: 'top', fontSize: 12, fontWeight: 'bold' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Age Column Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actual Age</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={ageChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={120}
              interval={0}
              tick={{ fontSize: 10 }}
            />
            <YAxis 
              label={{ value: 'Age (years.months)', angle: -90, position: 'insideLeft' }}
              domain={[0, 'auto']}
            />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'age') {
                  return [value, 'Age'];
                }
                return value;
              }}
            />
            <Legend />
            <Bar dataKey="age" fill="#3b82f6" name="Age" label={{ position: 'top', fontSize: 12, fontWeight: 'bold' }} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* BMI Charts Section */}
      {loadingBmi ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-gray-500">Loading BMI data...</div>
        </div>
      ) : bmiData.length > 0 ? (
        <div className="grid grid-cols-1 gap-6">
          {/* Height Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">📏 Class Height Overview (cm)</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={bmiData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Height (cm)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                          <p className="font-semibold text-gray-900">{payload[0].payload.fullName}</p>
                          <p className="text-blue-600">Height: {payload[0].value} cm</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="height" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 text-sm text-gray-600 text-center">
              Latest recorded height for {bmiData.length} students
            </div>
          </div>

          {/* Weight Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">⚖️ Class Weight Overview (kg)</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={[...bmiData].sort((a, b) => a.weight - b.weight)} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                          <p className="font-semibold text-gray-900">{payload[0].payload.fullName}</p>
                          <p className="text-green-600">Weight: {payload[0].value} kg</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="weight" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 text-sm text-gray-600 text-center">
              Latest recorded weight for {bmiData.length} students
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-4xl mb-2">📊</div>
          <p className="text-gray-500">No BMI data available for this class</p>
        </div>
      )}
    </div>
  );
};

// Pupils Details Tab - Detailed student information
const PupilsDetailsTab = ({ students, formId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name, age, house, gender
  const [medicalNotes, setMedicalNotes] = useState({}); // Store medical notes by student ID
  const [loadingMedical, setLoadingMedical] = useState(true);
  const [rewardsPoints, setRewardsPoints] = useState({}); // Store rewards points by student ID
  const [loadingRewards, setLoadingRewards] = useState(true);

  // Fetch rewards points for all students
  useEffect(() => {
    const fetchRewardsPoints = async () => {
      setLoadingRewards(true);
      try {
        const response = await fetch(buildApiUrl(`/api/rewards/form/${formId}`));
        const data = await response.json();
        
        const pointsMap = {};
        if (data.students) {
          data.students.forEach(student => {
            pointsMap[student.student_school_id] = student.total_points || 0;
          });
        }
        setRewardsPoints(pointsMap);
      } catch (error) {
        console.error('Error loading rewards points:', error);
      } finally {
        setLoadingRewards(false);
      }
    };

    if (formId) {
      fetchRewardsPoints();
    }
  }, [formId]);

  // Fetch medical notes for all students
  useEffect(() => {
    const fetchMedicalNotes = async () => {
      setLoadingMedical(true);
      try {
        const notesPromises = students.map(async (student) => {
          try {
            const response = await fetch(buildApiUrl(`/api/students/${student.school_id}/customFields`));
            const data = await response.json();
            
            if (data.customFields && data.customFields.length > 0) {
              // Find field 1080 (Medical condition notes)
              const notesField = data.customFields.find(f => f.id === 1080 || f.id === "1080");
              return {
                school_id: student.school_id,
                notes: notesField?.value || ""
              };
            }
            return { school_id: student.school_id, notes: "" };
          } catch (err) {
            console.error(`Error fetching medical notes for ${student.school_id}:`, err);
            return { school_id: student.school_id, notes: "" };
          }
        });

        const results = await Promise.all(notesPromises);
        const notesMap = {};
        results.forEach(result => {
          notesMap[result.school_id] = result.notes;
        });
        setMedicalNotes(notesMap);
      } catch (error) {
        console.error('Error loading medical notes:', error);
      } finally {
        setLoadingMedical(false);
      }
    };

    if (students.length > 0) {
      fetchMedicalNotes();
    }
  }, [students]);

  const filteredStudents = students.filter(student => 
    student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.school_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.academic_house?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    switch(sortBy) {
      case "name":
        return (a.full_name || "").localeCompare(b.full_name || "");
      case "age":
        return new Date(a.dob) - new Date(b.dob);
      case "house":
        return (a.academic_house || "").localeCompare(b.academic_house || "");
      case "gender":
        return (a.gender || "").localeCompare(b.gender || "");
      default:
        return 0;
    }
  });

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    if (months < 0) {
      years--;
      months += 12;
    }
    return `${years}y ${months}m`;
  };

  const HOUSE_COLORS = {
    "Aqsa": "bg-green-100 text-green-800 border-green-300",
    "Quds": "bg-blue-100 text-blue-800 border-blue-300",
    "Kaaba": "bg-purple-100 text-purple-800 border-purple-300",
    "Medina": "bg-orange-100 text-orange-800 border-orange-300"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">👥 Pupils Details</h2>
          <p className="text-gray-600 mt-1">Detailed information about {students.length} pupils in Form {formId}</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="🔍 Search by name, ID, or house..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">Sort by Name</option>
              <option value="age">Sort by Age</option>
              <option value="house">Sort by House</option>
              <option value="gender">Sort by Gender</option>
            </select>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  House
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Rewards
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medical Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedStudents.length > 0 ? (
                sortedStudents.map((student) => (
                  <tr key={student.school_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {student.full_name?.charAt(0) || "?"}
                          </div>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => window.location.href = `/student/${student.school_id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                          >
                            {student.full_name || "Unknown"}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {student.academic_house ? (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${HOUSE_COLORS[student.academic_house] || "bg-gray-100 text-gray-800"}`}>
                          {student.academic_house}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {loadingRewards ? (
                          <span className="text-gray-400">Loading...</span>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-lg font-bold text-indigo-600">{rewardsPoints[student.school_id] || 0}</span>
                            <span className="text-xs text-gray-500">points</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {loadingMedical ? (
                          <span className="text-gray-400">Loading...</span>
                        ) : medicalNotes[student.school_id] ? (
                          <span className="bg-yellow-50 text-yellow-800 px-2 py-1 rounded text-xs" title={medicalNotes[student.school_id]}>
                            {medicalNotes[student.school_id].length > 30 
                              ? medicalNotes[student.school_id].substring(0, 30) + '...' 
                              : medicalNotes[student.school_id]}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          // Navigate to student page with notes tab
                          window.location.href = `/student/${student.school_id}`;
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        📝 Add Note
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="text-6xl mb-4">🔍</div>
                    <p className="text-gray-500 text-lg">No pupils found matching your search</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Summary Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{students.length}</div>
            <div className="text-sm text-gray-600 mt-1">Total Pupils</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">
              {students.filter(s => s.gender === "Male").length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Boys</div>
          </div>
          <div className="text-center p-4 bg-pink-50 rounded-lg">
            <div className="text-3xl font-bold text-pink-600">
              {students.filter(s => s.gender === "Female").length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Girls</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">
              {sortedStudents.length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Filtered Results</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeTab = ({ students, formHomeworks, submissions, onStudentClick }) => {
  const server = buildApiUrl("");
  const { teacherEmail } = useAuth();
  const [selectedChat, setSelectedChat] = useState('group'); // 'group' by default, or student ID
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentUnreadCounts, setStudentUnreadCounts] = useState({}); // Per-student unread counts
  
  // Load unread counts for each student
  useEffect(() => {
    const loadStudentUnreadCounts = async () => {
      try {
        const email = teacherEmail || localStorage.getItem('teacherEmail');
        if (!email) return;

        const response = await fetch(buildApiUrl(`/api/student-chat/teacher-unread/${email}`));
        const data = await response.json();
        
        // Check if we have per-student breakdown
        if (data?.unread_by_student) {
          setStudentUnreadCounts(data.unread_by_student);
        } else {
          // If no unread messages, clear all badges
          setStudentUnreadCounts({});
        }
      } catch (error) {
        console.error("Failed to load student unread counts:", error);
      }
    };

    loadStudentUnreadCounts();
    // Refresh every 5 seconds
    const interval = setInterval(loadStudentUnreadCounts, 5000);
    return () => clearInterval(interval);
  }, [teacherEmail, students]);
  
  // Handle new message event from ChatWindow (including messages_read event)
  const handleChatEvent = (event) => {
    if (event && event.type === 'messages_read') {
      // Immediately refresh unread counts when messages are marked as read
      const email = teacherEmail || localStorage.getItem('teacherEmail');
      if (email) {
        fetch(buildApiUrl(`/api/student-chat/teacher-unread/${email}`))
          .then(res => res.json())
          .then(data => {
            if (data?.unread_by_student) {
              setStudentUnreadCounts(data.unread_by_student);
            }
          })
          .catch(error => console.error("Failed to refresh unread counts:", error));
      }
    }
  };
  
  // Get avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      'bg-gradient-to-br from-blue-400 to-blue-600',
      'bg-gradient-to-br from-green-400 to-green-600', 
      'bg-gradient-to-br from-red-400 to-red-600',
      'bg-gradient-to-br from-yellow-400 to-yellow-600',
      'bg-gradient-to-br from-blue-400 to-cyan-600',
      'bg-gradient-to-br from-teal-400 to-teal-600',
      'bg-gradient-to-br from-orange-400 to-orange-600',
      'bg-gradient-to-br from-red-500 to-pink-500'
    ];
    const nameHash = name.split('').reduce((hash, char) => hash + char.charCodeAt(0), 0);
    return colors[nameHash % colors.length];
  };

  // Handle student thumbnail click
  const handleStudentClick = (student) => {
    setSelectedChat(student.school_id);
    setSelectedStudent(student);
  };

  // Handle class group click
  const handleGroupClick = () => {
    setSelectedChat('group');
    setSelectedStudent(null);
  };

  // Get formId from URL
  const formId = window.location.pathname.split('/').pop();

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-gray-50">
      {/* Left Sidebar - Thin pane with thumbnails */}
      <div className="w-32 bg-white border-r border-gray-200 flex flex-col py-4 gap-4 overflow-y-auto">
        {/* Class Group Thumbnail */}
        <div className="px-2">
          <button
            onClick={handleGroupClick}
            className={`w-full flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 ${
              selectedChat === 'group'
                ? 'bg-blue-100 ring-2 ring-blue-500'
                : 'hover:bg-gray-100'
            }`}
            title="Class Group Chat"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-red-600 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-1">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="text-xs text-center text-gray-600 font-medium">Class</span>
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-4 mx-2"></div>

        {/* Student Thumbnails */}
        <div className="space-y-3 px-2">
          {students.map((student) => {
            const studentId = student.school_id; // Use school_id as it's the ID used in chat
            const isSelected = selectedChat === studentId;
            const unreadCount = studentUnreadCounts[studentId] || 0;
            
            return (
              <button
                key={studentId}
                onClick={() => handleStudentClick(student)}
                className={`w-full flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 ${
                  isSelected
                    ? 'bg-blue-100 ring-2 ring-blue-500'
                    : 'hover:bg-gray-100'
                }`}
                title={student.full_name}
              >
                {/* Student Photo/Avatar */}
                <div className="relative w-14 h-14 mb-1">
                  <img 
                    src={`${server}/student-photo/${student.school_id}?v=3`}
                    alt={student.full_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm ${getAvatarColor(student.full_name)} border-2 border-gray-200`}
                    style={{ display: 'none' }}
                  >
                    {student.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  {/* Unread message badge */}
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3 shadow-lg border-2 border-white"></div>
                  )}
                </div>
                <span className="text-xs text-center text-gray-600 font-medium line-clamp-1">
                  {student.full_name.split(' ')[0]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right Pane - Chat Window */}
      <div className="flex-1 flex flex-col bg-white">
        <ChatWindow
          currentUserId={teacherEmail || localStorage.getItem('teacherEmail')}
          recipientId={selectedChat === 'group' ? null : selectedChat}
          recipientName={selectedStudent?.full_name || 'Class Group'}
          isGroup={selectedChat === 'group'}
          formId={formId}
          students={students}
          chatType="student"
          onNewMessage={handleChatEvent}
        />
      </div>
    </div>
  );
};

// ResourcesTab is imported from ./components/ResourcesTab.js

// Rewards Tab Component
const RewardsTab = ({ formId, students, teacherEmail }) => {
  const [rewardsData, setRewardsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPoints, setShowAddPoints] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [studentHistory, setStudentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [addPointsForm, setAddPointsForm] = useState({
    points: '',
    reason: '',
    category: 'behaviour'
  });
  const server = buildApiUrl("");

  // Load rewards data for all students in the form
  useEffect(() => {
    loadRewardsData();
  }, [formId]);

  const loadRewardsData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${server}/api/rewards/form/${formId}`);
      const data = await response.json();
      setRewardsData(data.students || []);
    } catch (error) {
      console.error("Failed to load rewards data:", error);
    }
    setLoading(false);
  };

  const openAddPointsModal = (student) => {
    setSelectedStudent(student);
    setShowAddPoints(true);
    setAddPointsForm({ points: '', reason: '', category: 'behaviour' });
  };

  const closeAddPointsModal = () => {
    setShowAddPoints(false);
    setSelectedStudent(null);
    setAddPointsForm({ points: '', reason: '', category: 'behaviour' });
  };

  const handleAddPoints = async () => {
    if (!addPointsForm.points || addPointsForm.points <= 0) {
      alert("Please enter a valid number of points");
      return;
    }

    try {
      const response = await fetch(`${server}/api/rewards/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_school_id: selectedStudent.student_school_id,
          student_name: selectedStudent.student_name,
          form: formId,
          points: parseInt(addPointsForm.points),
          reason: addPointsForm.reason,
          category: addPointsForm.category,
          awarded_by: teacherEmail || localStorage.getItem('teacherEmail'),
          awarded_by_name: localStorage.getItem('teacherName') || teacherEmail
        })
      });

      if (response.ok) {
        closeAddPointsModal();
        loadRewardsData(); // Reload the rewards data
        alert(`Successfully added ${addPointsForm.points} points to ${selectedStudent.student_name}`);
      } else {
        const error = await response.json();
        alert(`Failed to add points: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Failed to add points:", error);
      alert("Failed to add points. Please try again.");
    }
  };

  const openHistoryModal = async (student) => {
    setSelectedStudent(student);
    setShowHistory(true);
    setLoadingHistory(true);
    
    try {
      const response = await fetch(`${server}/api/rewards/student/${student.student_school_id}/history`);
      const data = await response.json();
      setStudentHistory(data.history || []);
    } catch (error) {
      console.error("Failed to load student history:", error);
      setStudentHistory([]);
    }
    setLoadingHistory(false);
  };

  const closeHistoryModal = () => {
    setShowHistory(false);
    setSelectedStudent(null);
    setStudentHistory([]);
  };

  const getLevelIcon = (level) => {
    const icons = {
      'Bronze': '🥉',
      'Silver': '🥈',
      'Gold': '🏆',
      'Platinum': '💎'
    };
    return icons[level] || '🏆';
  };

  const getLevelColor = (level) => {
    const colors = {
      'Bronze': 'bg-amber-700',
      'Silver': 'bg-gray-400',
      'Gold': 'bg-yellow-500',
      'Platinum': 'bg-purple-600'
    };
    return colors[level] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading rewards data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-4">Student Rewards</h2>
        <p className="text-gray-600 mb-6">Reward your students for good behaviour, contributions, and achievements</p>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Points</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Rewarded</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Times Rewarded</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rewardsData.map((student) => (
                <tr key={student.student_school_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={`${server}/student-photo-proxy/${student.student_school_id}`}
                          alt={student.student_name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                        <div className="text-sm text-gray-500">ID: {student.student_school_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-lg font-bold text-indigo-600">{student.total_points}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${getLevelColor(student.reward_level.name)}`}>
                      {getLevelIcon(student.reward_level.name)} {student.reward_level.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.last_reward_date 
                      ? new Date(student.last_reward_date).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.reward_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => openAddPointsModal(student)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                    >
                      Add Points
                    </button>
                    <button
                      onClick={() => openHistoryModal(student)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                    >
                      View History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Points Modal */}
      {showAddPoints && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Add Points to {selectedStudent.student_name}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Points</label>
                <input
                  type="number"
                  min="1"
                  value={addPointsForm.points}
                  onChange={(e) => setAddPointsForm({ ...addPointsForm, points: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter points (e.g., 10)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={addPointsForm.category}
                  onChange={(e) => setAddPointsForm({ ...addPointsForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="behaviour">Behaviour</option>
                  <option value="social">Social</option>
                  <option value="homework">Homework</option>
                  <option value="contribution">Contribution</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason (Optional)</label>
                <textarea
                  value={addPointsForm.reason}
                  onChange={(e) => setAddPointsForm({ ...addPointsForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Why are you rewarding this student?"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddPoints}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                >
                  Add Points
                </button>
                <button
                  onClick={closeAddPointsModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Reward History for {selectedStudent.student_name}</h3>
            
            {loadingHistory ? (
              <div className="text-center py-8 text-gray-600">Loading history...</div>
            ) : studentHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No reward history yet</div>
            ) : (
              <div className="space-y-3">
                {studentHistory.map((record, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-bold text-green-600 text-lg">+{record.points} points</span>
                        <span className="ml-3 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {record.category}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(record.awarded_at).toLocaleString()}
                      </div>
                    </div>
                    {record.reason && (
                      <div className="text-sm text-gray-700 mb-2">
                        <strong>Reason:</strong> {record.reason}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Awarded by: {record.awarded_by_name || record.awarded_by}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={closeHistoryModal}
                className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TimetableTab = ({ formId, students }) => {
  const [timetables, setTimetables] = useState([]);
  const [loadingTimetables, setLoadingTimetables] = useState(true);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const server = buildApiUrl("");  // ✅ Use config instead of localhost

  // ✅ NEW: Timetable template configuration with manual slot editing
  const [templateConfig, setTemplateConfig] = useState({
    startTime: { hours: 7, minutes: 10 },     // 7:10 AM
    endTime: { hours: 13, minutes: 45 },      // 1:45 PM
    lessonDuration: 50,                       // 50 minutes
    breakDuration: 20,                        // 20 minutes
    includeBreaks: true,
    manualSlots: [],                          // For custom slot-by-slot editing
    periods: []                               // NEW: Custom periods for weekly timetables
  });

  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateMode, setTemplateMode] = useState('periods'); // 'auto', 'manual', or 'periods'
  const [editingSlot, setEditingSlot] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]); // ✅ NEW: State for managing time slots
  
  // NEW: Weekly timetable state
  const [showWeeklyEditor, setShowWeeklyEditor] = useState(false);
  const [weeklyTimetable, setWeeklyTimetable] = useState({
    name: '',
    description: '',
    assignedStudents: [],
    days: {
      Sunday: [],
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: []
    }
  });

  // ✅ State for selecting periods to merge in weekly timetable creator
  const [weeklyMergeSelections, setWeeklyMergeSelections] = useState({
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: []
  });

  // ✅ NEW: Admin-defined subjects and rooms
  const [adminSubjects, setAdminSubjects] = useState([]);
  const [adminRooms, setAdminRooms] = useState([]);
  const [loadingAdminData, setLoadingAdminData] = useState(false);
  
  // 👥 NEW: Staff data for teacher dropdown
  const [staffData, setStaffData] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  
  // ✅ NEW: Timetable editing state
  const [editingTimetable, setEditingTimetable] = useState(null);
  const [editTimetableData, setEditTimetableData] = useState(null);
  
  // ✅ NEW: State for selecting periods to merge
  const [selectedPeriodsForMerge, setSelectedPeriodsForMerge] = useState({
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: []
  });

  // ✅ Helper function to get subject color
  const getSubjectColor = (subjectName) => {
    if (!subjectName) return '#E5E7EB'; // Default gray for unassigned
    
    const subject = adminSubjects.find(s => s.name === subjectName);
    return subject?.color || '#3B82F6'; // Default blue if subject not found or no color
  };

  // ✅ Helper function to get period display color
  const getPeriodDisplayColor = (period, assignment = null) => {
    // For non-lesson periods (break, lunch, assembly, study), use the period's own color
    if (period.type !== 'lesson') {
      return period.color || '#F59E0B';
    }
    
    // For lessons, use the subject color from assignment, or default
    if (assignment && assignment.subject) {
      return getSubjectColor(assignment.subject);
    }
    
    // If no assignment, show gray
    return '#E5E7EB';
  };

  // ✅ ENHANCED: Configurable time slot generator with manual mode support
  const generateDefaultTimeSlots = (config = templateConfig) => {
    // If in periods mode, convert periods to slots format
    if (templateMode === 'periods' && config.periods && config.periods.length > 0) {
      return config.periods.map(period => ({
        id: period.id,
        startTime: period.startTime,
        endTime: period.endTime,
        type: period.type,
        subject: period.type === 'lesson' ? '' : period.name,
        teacher: '',
        room: ''
      }));
    }
    
    // If in manual mode, return the manually created slots
    if (templateMode === 'manual' && config.manualSlots && config.manualSlots.length > 0) {
      return config.manualSlots;
    }
    
    // Auto-generation mode
    const slots = [];
    let currentTime = new Date();
    currentTime.setHours(config.startTime.hours, config.startTime.minutes, 0, 0);
    
    const endTime = new Date();
    endTime.setHours(config.endTime.hours, config.endTime.minutes, 0, 0);
    
    let slotNumber = 1;
    
    while (currentTime < endTime) {
      // Add lesson slot
      const lessonEnd = new Date(currentTime.getTime() + config.lessonDuration * 60000);
      
      if (lessonEnd <= endTime) {
        slots.push({
          id: `slot-${slotNumber}`,
          startTime: currentTime.toTimeString().slice(0, 5),
          endTime: lessonEnd.toTimeString().slice(0, 5),
          type: 'lesson',
          subject: '',
          teacher: '',
          room: ''
        });
        
        currentTime = new Date(lessonEnd.getTime());
        slotNumber++;
        
        // Add break if enabled and not the last slot
        if (config.includeBreaks) {
          const breakEnd = new Date(currentTime.getTime() + config.breakDuration * 60000);
          if (breakEnd < endTime) {
            slots.push({
              id: `break-${slotNumber}`,
              startTime: currentTime.toTimeString().slice(0, 5),
              endTime: breakEnd.toTimeString().slice(0, 5),
              type: 'break',
              subject: 'Break',
              teacher: '',
              room: ''
            });
            currentTime = new Date(breakEnd.getTime());
          }
        }
      } else {
        break;
      }
    }
    
    return slots;
  };

  // ✅ NEW: Manual slot management functions
  const addNewSlot = () => {
    const existingSlots = templateConfig.manualSlots || [];
    const lastSlot = existingSlots[existingSlots.length - 1];
    
    // Default start time based on last slot or template start time
    let startTime = '07:10';
    if (lastSlot) {
      startTime = lastSlot.endTime;
    } else if (templateConfig.startTime) {
      startTime = `${String(templateConfig.startTime.hours).padStart(2, '0')}:${String(templateConfig.startTime.minutes).padStart(2, '0')}`;
    }
    
    // Calculate default end time (50 minutes later)
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + 50 * 60000);
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    
    const newSlot = {
      id: `manual-slot-${Date.now()}`,
      startTime,
      endTime,
      type: 'lesson',
      subject: '',
      teacher: '',
      room: ''
    };
    
    const updatedConfig = {
      ...templateConfig,
      manualSlots: [...existingSlots, newSlot]
    };
    
    setTemplateConfig(updatedConfig);
    updateTemplate(updatedConfig);
  };

  const removeSlot = (slotId) => {
    const updatedSlots = (templateConfig.manualSlots || []).filter(slot => slot.id !== slotId);
    const updatedConfig = {
      ...templateConfig,
      manualSlots: updatedSlots
    };
    
    setTemplateConfig(updatedConfig);
    updateTemplate(updatedConfig);
  };

  const updateSlot = (slotId, field, value) => {
    const updatedSlots = (templateConfig.manualSlots || []).map(slot =>
      slot.id === slotId ? { ...slot, [field]: value } : slot
    );
    
    const updatedConfig = {
      ...templateConfig,
      manualSlots: updatedSlots
    };
    
    setTemplateConfig(updatedConfig);
    updateTemplate(updatedConfig);
  };

  const switchToManualMode = () => {
    // Convert current auto-generated slots to manual slots
    const currentSlots = generateDefaultTimeSlots(templateConfig);
    const manualSlots = currentSlots.map((slot, index) => ({
      ...slot,
      id: `manual-slot-${Date.now()}-${index}`
    }));
    
    const updatedConfig = {
      ...templateConfig,
      manualSlots
    };
    
    setTemplateConfig(updatedConfig);
    setTemplateMode('manual');
    updateTemplate(updatedConfig);
  };

  const switchToAutoMode = () => {
    setTemplateMode('auto');
    const updatedConfig = {
      ...templateConfig,
      manualSlots: []
    };
    setTemplateConfig(updatedConfig);
    updateTemplate(updatedConfig);
  };

  // ✅ NEW: Period management functions for full control
  const addNewPeriod = () => {
    const existingPeriods = templateConfig.periods || [];
    const lastPeriod = existingPeriods[existingPeriods.length - 1];
    
    // Default start time based on last period or template start time
    let startTime = '07:10';
    if (lastPeriod) {
      startTime = lastPeriod.endTime;
    }
    
    // Calculate default end time (50 minutes later)
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + 50 * 60000);
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    
    const newPeriod = {
      id: `period-${Date.now()}`,
      name: `Period ${existingPeriods.length + 1}`,
      startTime,
      endTime,
      type: 'lesson'
      // Color will be determined by selected subject
    };
    
    const updatedConfig = {
      ...templateConfig,
      periods: [...existingPeriods, newPeriod]
    };
    
    setTemplateConfig(updatedConfig);
  };

  // ✅ NEW: Quick add 15-minute period following the previous session
  const addQuick15MinPeriod = () => {
    const existingPeriods = templateConfig.periods || [];
    const lastPeriod = existingPeriods[existingPeriods.length - 1];
    
    // Default start time based on last period's end time or template start time
    let startTime = '07:10';
    if (lastPeriod) {
      // Start immediately after the last period
      startTime = lastPeriod.endTime;
    }
    
    // Calculate end time (15 minutes later)
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + 15 * 60000); // 15 minutes
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    
    const newPeriod = {
      id: `period-${Date.now()}`,
      name: `Period ${existingPeriods.length + 1}`,
      startTime,
      endTime,
      type: 'lesson'
    };
    
    const updatedConfig = {
      ...templateConfig,
      periods: [...existingPeriods, newPeriod]
    };
    
    setTemplateConfig(updatedConfig);
  };

  // ✅ NEW: Reload global template from backend
  const reloadGlobalTemplate = async () => {
    try {
      const templateRes = await fetch(`${server}/admin-global-template`);
      if (templateRes.ok) {
        const globalTemplate = await templateRes.json();
        console.log('📥 Reloaded global template from backend:', globalTemplate);
        
        if (globalTemplate.periods && globalTemplate.periods.length > 0) {
          setTemplateConfig(prev => ({
            ...prev,
            ...globalTemplate
          }));
          setTemplateMode('periods');
        } else {
          // If no periods, keep current template but update basic settings
          setTemplateConfig(prev => ({
            ...prev,
            startTime: globalTemplate.startTime || prev.startTime,
            endTime: globalTemplate.endTime || prev.endTime,
            lessonDuration: globalTemplate.lessonDuration || prev.lessonDuration,
            breakDuration: globalTemplate.breakDuration || prev.breakDuration,
            includeBreaks: globalTemplate.includeBreaks !== undefined ? globalTemplate.includeBreaks : prev.includeBreaks
          }));
        }
      }
    } catch (error) {
      console.error('Error reloading global template:', error);
    }
  };

  const removePeriod = (periodId) => {
    const updatedPeriods = (templateConfig.periods || []).filter(period => period.id !== periodId);
    const updatedConfig = {
      ...templateConfig,
      periods: updatedPeriods
    };
    setTemplateConfig(updatedConfig);
  };

  const updatePeriod = (periodId, field, value) => {
    const updatedPeriods = (templateConfig.periods || []).map(period =>
      period.id === periodId ? { ...period, [field]: value } : period
    );
    
    const updatedConfig = {
      ...templateConfig,
      periods: updatedPeriods
    };
    setTemplateConfig(updatedConfig);
  };

  const switchToPeriodsMode = () => {
    setTemplateMode('periods');
    // Initialize with some default periods if none exist
    if (!templateConfig.periods || templateConfig.periods.length === 0) {
      const defaultPeriods = [
        { id: 'period-1', name: 'Period 1', startTime: '07:10', endTime: '08:00', type: 'lesson' },
        { id: 'break-1', name: 'Break', startTime: '08:00', endTime: '08:20', type: 'break', color: '#F59E0B' },
        { id: 'period-2', name: 'Period 2', startTime: '08:20', endTime: '09:10', type: 'lesson' },
        { id: 'period-3', name: 'Period 3', startTime: '09:10', endTime: '10:00', type: 'lesson' },
        { id: 'break-2', name: 'Recess', startTime: '10:00', endTime: '10:30', type: 'break', color: '#F59E0B' },
        { id: 'period-4', name: 'Period 4', startTime: '10:30', endTime: '11:20', type: 'lesson' },
        { id: 'period-5', name: 'Period 5', startTime: '11:20', endTime: '12:10', type: 'lesson' },
        { id: 'period-6', name: 'Period 6', startTime: '12:10', endTime: '13:00', type: 'lesson' }
      ];
      
      const updatedConfig = {
        ...templateConfig,
        periods: defaultPeriods
      };
      setTemplateConfig(updatedConfig);
    }
  };

  // ✅ NEW: Weekly timetable functions
  const assignPeriodToDay = (day, periodId, subject = '', teacher = '', room = '') => {
    const period = templateConfig.periods.find(p => p.id === periodId);
    if (!period) return;

    const newAssignment = {
      id: `${day}-${periodId}-${Date.now()}`,
      periodId,
      period: period,
      subject,
      teacher,
      room
    };

    setWeeklyTimetable(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: [...prev.days[day], newAssignment]
      }
    }));
  };

  // ✅ NEW: Auto-populate all periods to each day
  const autoPopulateWeeklyTimetable = () => {
    console.log('🔄 Auto-populating weekly timetable with template periods:', templateConfig.periods);
    
    // Include ALL periods (lessons, breaks, lunch, etc.) - not just lessons
    const allPeriods = templateConfig.periods || [];
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];
    
    if (allPeriods.length === 0) {
      console.warn('⚠️ No template periods found to populate');
      return;
    }
    
    const newDays = {};
    days.forEach(day => {
      newDays[day] = allPeriods.map((period, index) => ({
        id: `${day}-${period.id}-${Date.now()}-${Math.random()}-${index}`,
        periodId: period.id,
        period: { ...period }, // Create a copy to avoid reference issues
        subject: period.type === 'lesson' ? '' : (period.name || period.type), // Pre-fill non-lessons
        teacher: '',
        room: ''
      }));
    });

    console.log('✅ Populated weekly timetable days:', newDays);

    setWeeklyTimetable(prev => ({
      ...prev,
      days: newDays
    }));
  };

  // ✅ NEW: Open weekly editor with auto-populated periods
  const openWeeklyEditor = () => {
    console.log('🚀 Opening weekly editor with template periods:', templateConfig.periods?.length || 0);
    
    // Reset weekly timetable state
    setWeeklyTimetable({
      name: '',
      description: '',
      assignedStudents: [],
      days: {
        Sunday: [],
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: []
      }
    });
    
    setShowWeeklyEditor(true);
    
    // Auto-populate with all template periods immediately if available
    if (templateConfig.periods && templateConfig.periods.length > 0) {
      console.log('✅ Template periods available, auto-populating immediately');
      setTimeout(() => {
        autoPopulateWeeklyTimetable();
      }, 50); // Shorter delay for better UX
    } else {
      console.warn('⚠️ No template periods found. Please create periods in the template first.');
    }
  };

  const removePeriodFromDay = (day, assignmentId) => {
    setWeeklyTimetable(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: prev.days[day].filter(assignment => assignment.id !== assignmentId)
      }
    }));
  };

  const updateDayAssignment = (day, assignmentId, field, value) => {
    setWeeklyTimetable(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: prev.days[day].map(assignment =>
          assignment.id === assignmentId ? { ...assignment, [field]: value } : assignment
        )
      }
    }));
  };

  // ✅ Merge selected periods in weekly timetable creator
  const mergeWeeklyPeriods = (day) => {
    const selectedIds = weeklyMergeSelections[day] || [];
    
    if (selectedIds.length < 2) {
      alert('Please select at least 2 periods to merge');
      return;
    }

    const dayAssignments = weeklyTimetable.days[day];
    const selectedAssignments = dayAssignments
      .filter(a => selectedIds.includes(a.id))
      .sort((a, b) => {
        const timeA = a.period.startTime.split(':').map(Number);
        const timeB = b.period.startTime.split(':').map(Number);
        return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
      });

    // Validate consecutive periods
    for (let i = 0; i < selectedAssignments.length - 1; i++) {
      const current = selectedAssignments[i];
      const next = selectedAssignments[i + 1];
      
      if (current.period.endTime !== next.period.startTime) {
        alert('Selected periods must be consecutive (no gaps between them)');
        return;
      }
    }

    // Create merged period
    const firstPeriod = selectedAssignments[0];
    const lastPeriod = selectedAssignments[selectedAssignments.length - 1];
    const subjectName = selectedAssignments[0].subject || 'Merged Session';
    
    const mergedAssignment = {
      id: `merged-${Date.now()}`,
      period: {
        id: `merged-period-${Date.now()}`,
        name: subjectName,
        startTime: firstPeriod.period.startTime,
        endTime: lastPeriod.period.endTime,
        type: 'lesson',
        color: firstPeriod.period.color || '#3B82F6',
        isMerged: true,
        originalPeriods: selectedAssignments.map(a => a.period)
      },
      subject: firstPeriod.subject || '',
      room: firstPeriod.room || '',
      teacher: firstPeriod.teacher || ''
    };

    // Update weekly timetable
    setWeeklyTimetable(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: [
          ...prev.days[day].filter(a => !selectedIds.includes(a.id)),
          mergedAssignment
        ].sort((a, b) => {
          const timeA = a.period.startTime.split(':').map(Number);
          const timeB = b.period.startTime.split(':').map(Number);
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        })
      }
    }));

    // Clear selection
    setWeeklyMergeSelections(prev => ({
      ...prev,
      [day]: []
    }));

    alert(`✅ Successfully merged ${selectedIds.length} periods into one!`);
  };

  // ✅ Unmerge a merged period in weekly timetable creator
  const unmergeWeeklyPeriod = (day, mergedAssignmentId) => {
    const dayAssignments = weeklyTimetable.days[day];
    const mergedAssignment = dayAssignments.find(a => a.id === mergedAssignmentId);
    
    if (!mergedAssignment?.period?.isMerged) {
      alert('This period is not a merged period');
      return;
    }

    // Restore original periods
    const originalPeriods = mergedAssignment.period.originalPeriods || [];
    const restoredAssignments = originalPeriods.map(period => ({
      id: `restored-${period.id}-${Date.now()}`,
      period: { ...period, isMerged: false, originalPeriods: undefined },
      subject: mergedAssignment.subject,
      room: mergedAssignment.room,
      teacher: mergedAssignment.teacher
    }));

    setWeeklyTimetable(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: [
          ...prev.days[day].filter(a => a.id !== mergedAssignmentId),
          ...restoredAssignments
        ].sort((a, b) => {
          const timeA = a.period.startTime.split(':').map(Number);
          const timeB = b.period.startTime.split(':').map(Number);
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        })
      }
    }));

    alert(`✅ Successfully unmerged period into ${originalPeriods.length} original periods!`);
  };

  const [newTimetable, setNewTimetable] = useState({
    name: '',
    description: '',
    assignedStudents: [],
    slots: []
  });

  // ✅ Function to update template and regenerate slots
  const updateTemplate = (newConfig) => {
    setTemplateConfig(newConfig);
    const newSlots = generateDefaultTimeSlots(newConfig);
    setNewTimetable(prev => ({
      ...prev,
      slots: newSlots
    }));
    
    // ✅ NEW: Save template configuration to backend
    if (formId) {
      fetch(`${server}/save-template-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formId,
          config: newConfig
        })
      })
      .then(res => {
        if (res.ok) {
          console.log('✅ Template configuration saved');
        } else {
          console.error('❌ Failed to save template configuration');
        }
      })
      .catch(err => console.error('❌ Error saving template configuration:', err));
    }
  };

  // ✅ Reset to default template
  const resetToDefaultTemplate = () => {
    const defaultConfig = {
      startTime: { hours: 7, minutes: 10 },
      endTime: { hours: 13, minutes: 45 },
      lessonDuration: 50,
      breakDuration: 20,
      includeBreaks: true
    };
    updateTemplate(defaultConfig);
  };

  // Load existing timetables
  useEffect(() => {
    setLoadingTimetables(true);
    fetch(`${server}/timetables/${formId}`)
      .then(res => res.json())
      .then(data => {
        setTimetables(data.timetables || []);
        setLoadingTimetables(false);
      })
      .catch(err => {
        console.error("Failed to load timetables:", err);
        setLoadingTimetables(false);
      });
  }, [formId]);

  // ✅ NEW: Load admin global template instead of form-specific template
  useEffect(() => {
    if (formId) {
      // Load the admin global template instead of form-specific template
      fetch(`${server}/admin-global-template`)
        .then(res => {
          if (res.ok) {
            return res.json();
          } else if (res.status === 404) {
            // No admin template exists yet, use default
            return null;
          }
          throw new Error(`HTTP ${res.status}`);
        })
        .then(data => {
          if (data) {
            console.log('📖 Loaded admin global template:', data);
            
            // ✅ Check if admin template has periods, if not provide defaults
            if (data.periods && data.periods.length > 0) {
              setTemplateConfig(data);
              setTemplateMode('periods');
            } else {
              console.log('📝 Admin template has no periods, adding default periods');
              // Use admin template settings but add default periods
              const templateWithDefaultPeriods = {
                ...data,
                periods: [
                  {
                    id: 'period-1',
                    name: 'Period 1',
                    startTime: '07:10',
                    endTime: '08:00',
                    type: 'lesson'
                  },
                  {
                    id: 'break-1',
                    name: 'Morning Break',
                    startTime: '08:00',
                    endTime: '08:20',
                    type: 'break',
                    color: '#F59E0B'
                  },
                  {
                    id: 'period-2',
                    name: 'Period 2',
                    startTime: '08:20',
                    endTime: '09:10',
                    type: 'lesson'
                  },
                  {
                    id: 'break-2',
                    name: 'Mid Break',
                    startTime: '09:10',
                    endTime: '09:30',
                    type: 'break',
                    color: '#F59E0B'
                  },
                  {
                    id: 'period-3',
                    name: 'Period 3',
                    startTime: '09:30',
                    endTime: '10:20',
                    type: 'lesson'
                  },
                  {
                    id: 'period-4',
                    name: 'Period 4',
                    startTime: '10:20',
                    endTime: '11:10',
                    type: 'lesson'
                  }
                ]
              };
              setTemplateConfig(templateWithDefaultPeriods);
              setTemplateMode('periods');
            }
          } else {
            console.log('📝 No admin template found, using basic default');
            // Set basic default template
            const defaultConfig = {
              startTime: { hours: 7, minutes: 10 },
              endTime: { hours: 13, minutes: 45 },
              lessonDuration: 50,
              breakDuration: 20,
              includeBreaks: true,
              manualSlots: [],
              periods: [
                {
                  id: 'period-1',
                  name: 'Period 1',
                  startTime: '07:10',
                  endTime: '08:00',
                  type: 'lesson',
                  color: '#3B82F6'
                },
                {
                  id: 'break-1',
                  name: 'Morning Break',
                  startTime: '08:00',
                  endTime: '08:20',
                  type: 'break',
                  color: '#F59E0B'
                },
                {
                  id: 'period-2',
                  name: 'Period 2',
                  startTime: '08:20',
                  endTime: '09:10',
                  type: 'lesson',
                  color: '#10B981'
                }
              ]
            };
            setTemplateConfig(defaultConfig);
            setTemplateMode('periods');
          }
        })
        .catch(error => {
          console.error('❌ Error loading admin template:', error);
          // Fallback to basic default with periods
          const defaultConfig = {
            startTime: { hours: 7, minutes: 10 },
            endTime: { hours: 13, minutes: 45 },
            lessonDuration: 50,
            breakDuration: 20,
            includeBreaks: true,
            manualSlots: [],
            periods: [
              {
                id: 'period-1',
                name: 'Period 1',
                startTime: '07:10',
                endTime: '08:00',
                type: 'lesson'
              },
              {
                id: 'break-1',
                name: 'Morning Break',
                startTime: '08:00',
                endTime: '08:20',
                type: 'break',
                color: '#F59E0B'
              },
              {
                id: 'period-2',
                name: 'Period 2',
                startTime: '08:20',
                endTime: '09:10',
                type: 'lesson'
              },
              {
                id: 'break-2',
                name: 'Mid Break',
                startTime: '09:10',
                endTime: '09:30',
                type: 'break',
                color: '#F59E0B'
              },
              {
                id: 'period-3',
                name: 'Period 3',
                startTime: '09:30',
                endTime: '10:20',
                type: 'lesson'
              }
            ]
          };
          setTemplateConfig(defaultConfig);
          setTemplateMode('periods');
        });
    }
  }, [formId]);

  // ✅ Update newTimetable slots when templateConfig changes
  useEffect(() => {
    if (templateConfig && (templateConfig.periods?.length > 0 || templateConfig.manualSlots?.length > 0 || templateMode === 'auto')) {
      const newSlots = generateDefaultTimeSlots(templateConfig);
      setNewTimetable(prev => ({
        ...prev,
        slots: newSlots
      }));
    }
  }, [templateConfig, templateMode]);

  // ✅ NEW: Load admin-defined subjects and rooms
  useEffect(() => {
    const loadAdminData = async () => {
      setLoadingAdminData(true);
      try {
        // Load subjects
        const subjectsRes = await fetch(`${server}/admin-subjects`);
        if (subjectsRes.ok) {
          const subjectsData = await subjectsRes.json();
          setAdminSubjects(subjectsData);
        }

        // Load rooms
        const roomsRes = await fetch(`${server}/admin-rooms`);
        if (roomsRes.ok) {
          const roomsData = await roomsRes.json();
          setAdminRooms(roomsData);
        }

        // Load global template to sync with admin settings
        const templateRes = await fetch(`${server}/admin-global-template`);
        if (templateRes.ok) {
          const globalTemplate = await templateRes.json();
          // Optional: Update local template with global settings if they exist
          if (globalTemplate.periods && globalTemplate.periods.length > 0) {
            setTemplateConfig(prev => ({
              ...prev,
              ...globalTemplate
            }));
            setTemplateMode('periods');
          }
        }
      } catch (error) {
        console.error('Error loading admin data:', error);
      } finally {
        setLoadingAdminData(false);
      }
    };

    loadAdminData();
  }, []); // Load once on component mount

  // 👥 NEW: Load staff data for teacher dropdown
  useEffect(() => {
    const loadStaffData = async () => {
      setLoadingStaff(true);
      try {
        // Import config to get current school
        const { currentSchool } = await import('./config');
        // Use staff-demo.json for demo school, staff.json for production
        const staffFile = currentSchool === 'demo' ? 'staff-demo.json' : 'staff.json';
        console.log(`👥 Loading staff from: ${staffFile} (school: ${currentSchool})`);
        
        // Load staff from public directory
        const response = await fetch(`/${staffFile}`);
        if (response.ok) {
          const data = await response.json();
          console.log('👥 Loaded staff data:', data.length, 'teachers');
          setStaffData(data);
        } else {
          console.error('Failed to load staff data');
        }
      } catch (error) {
        console.error('Error loading staff data:', error);
      } finally {
        setLoadingStaff(false);
      }
    };

    loadStaffData();
  }, []); // Load once on component mount

  // ✅ NEW: Create weekly timetable function
  const handleCreateWeeklyTimetable = async () => {
    console.log('🔄 Creating weekly timetable...', weeklyTimetable);
    
    // Validation
    if (!weeklyTimetable.name.trim()) {
      alert('Please enter a weekly timetable name');
      return;
    }
    
    try {
      const payload = {
        ...weeklyTimetable,
        formId,
        periods: templateConfig.periods,
        createdBy: localStorage.getItem('teacherEmail') || 'Unknown Teacher'
      };
      
      console.log('📤 Sending weekly timetable payload:', payload);
      console.log('🌐 Backend URL:', `${server}/create-weekly-timetable`);

      const response = await fetch(`${server}/create-weekly-timetable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Weekly timetable created successfully:', result);
        // Refresh timetables list
        fetch(`${server}/timetables/${formId}`)
          .then(res => res.json())
          .then(data => setTimetables(data.timetables || []))
          .catch(err => console.error("Failed to refresh timetables:", err));
        
        setShowWeeklyEditor(false);
        // Reset form
        setWeeklyTimetable({
          name: '',
          description: '',
          assignedStudents: [],
          days: {
            Sunday: [],
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: []
          }
        });
      } else {
        const errorData = await response.text();
        console.error('❌ Failed to create weekly timetable:');
        console.error('   Status:', response.status, response.statusText);
        console.error('   Error:', errorData);
        console.error('   URL:', `${server}/create-weekly-timetable`);
        alert(`Failed to create weekly timetable: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Error creating weekly timetable:', error);
      alert(`Failed to create weekly timetable: ${error.message}`);
    }
  };

  const handleUpdateSlot = (slotId, field, value) => {
    setNewTimetable(prev => ({
      ...prev,
      slots: prev.slots.map(slot =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const handleStudentAssignment = (studentId) => {
    setNewTimetable(prev => ({
      ...prev,
      assignedStudents: prev.assignedStudents.includes(studentId)
        ? prev.assignedStudents.filter(id => id !== studentId)
        : [...prev.assignedStudents, studentId]
    }));
  };

  const handleDeleteTimetable = async (timetableId) => {
    if (!confirm('Are you sure you want to delete this timetable?')) return;
    
    try {
      await fetch(`${server}/delete-timetable/${timetableId}`, { method: 'DELETE' });
      setTimetables(timetables.filter(t => t.id !== timetableId));
      alert('Timetable deleted successfully!');
    } catch (error) {
      console.error('Error deleting timetable:', error);
      alert('Failed to delete timetable');
    }
  };

  // ✅ NEW: Edit timetable function
  const handleEditTimetable = (timetable) => {
    console.log('🔄 Editing timetable:', timetable.id);
    setEditingTimetable(timetable.id);
    
    // Create a deep copy for editing, especially for weekly timetables
    const deepCopyDays = {};
    if (timetable.days) {
      Object.keys(timetable.days).forEach(day => {
        deepCopyDays[day] = timetable.days[day].map(assignment => ({
          ...assignment,
          period: { ...assignment.period }
        }));
      });
    }
    
    setEditTimetableData({
      ...timetable,
      days: deepCopyDays
    });
    
    // Auto-expand the timetable view to show edit interface
    const weeklyIndex = timetables.filter(t => t.type === 'weekly').findIndex(t => t.id === timetable.id);
    if (weeklyIndex !== -1) {
      setSelectedTimetable(`weekly-${weeklyIndex}`);
    } else {
      const dailyIndex = timetables.filter(t => t.type !== 'weekly').findIndex(t => t.id === timetable.id);
      if (dailyIndex !== -1) {
        setSelectedTimetable(`daily-${dailyIndex}`);
      }
    }
  };

  // ✅ NEW: Save edited timetable
  const saveEditedTimetable = async () => {
    if (!editTimetableData) return;
    
    console.log('🔄 Saving timetable:', editTimetableData);
    
    try {
      const endpoint = editTimetableData.type === 'weekly' 
        ? `${server}/weekly-timetable/${editTimetableData.id}`
        : `${server}/timetable/${editTimetableData.id}`;
        
      const requestBody = editTimetableData.type === 'weekly' ? {
        name: editTimetableData.name,
        description: editTimetableData.description,
        assignedStudents: editTimetableData.assignedStudents || [],
        days: editTimetableData.days,
        formId: formId,
        createdBy: editTimetableData.createdBy
      } : {
        name: editTimetableData.name,
        description: editTimetableData.description,
        assignedStudents: editTimetableData.assignedStudents || [],
        slots: editTimetableData.slots || [],
        formId: formId,
        createdBy: editTimetableData.createdBy
      };

      console.log('🔄 Endpoint:', endpoint);
      console.log('🔄 Request body:', requestBody);

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('🔄 Save response status:', response.status);
      console.log('🔄 Save response headers:', response.headers);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Save successful:', result);
        
        // Refresh timetables
        const updatedTimetables = timetables.map(t => 
          t.id === editTimetableData.id ? editTimetableData : t
        );
        setTimetables(updatedTimetables);
        
        // Close edit mode
        setEditingTimetable(null);
        setEditTimetableData(null);
        
        // Clear merge selections
        setSelectedPeriodsForMerge({
          Sunday: [],
          Monday: [],
          Tuesday: [],
          Wednesday: [],
          Thursday: []
        });
        
        alert('Timetable updated successfully!');
      } else {
        const errorText = await response.text();
        console.error('❌ Save failed:', response.status, errorText);
        alert(`Failed to save timetable changes: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error saving timetable:', error);
      alert(`Failed to save timetable changes: ${error.message}`);
    }
  };

  // ✅ NEW: Cancel edit
  const cancelEditTimetable = () => {
    setEditingTimetable(null);
    setEditTimetableData(null);
    // Clear merge selections
    setSelectedPeriodsForMerge({
      Sunday: [],
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: []
    });
  };

  // ✅ NEW: Remove period from day (for editing)
  const removePeriodFromEditTimetable = (day, assignmentId) => {
    setEditTimetableData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: prev.days[day].filter(assignment => assignment.id !== assignmentId)
      }
    }));
  };

  // ✅ NEW: Update assignment subject/room (for editing)
  const updateAssignmentInEditTimetable = (day, assignmentId, field, value) => {
    setEditTimetableData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: prev.days[day].map(assignment => 
          assignment.id === assignmentId 
            ? { ...assignment, [field]: value }
            : assignment
        )
      }
    }));
  };

  // ✅ NEW: Toggle period selection for merging
  const togglePeriodSelection = (day, assignmentId) => {
    setSelectedPeriodsForMerge(prev => {
      const currentSelected = prev[day] || [];
      const isSelected = currentSelected.includes(assignmentId);
      
      return {
        ...prev,
        [day]: isSelected 
          ? currentSelected.filter(id => id !== assignmentId)
          : [...currentSelected, assignmentId]
      };
    });
  };

  // ✅ NEW: Merge selected periods
  const mergeSelectedPeriods = (day) => {
    const selectedIds = selectedPeriodsForMerge[day] || [];
    
    if (selectedIds.length < 2) {
      alert('Please select at least 2 periods to merge');
      return;
    }

    const dayAssignments = editTimetableData.days[day];
    const selectedAssignments = dayAssignments
      .filter(a => selectedIds.includes(a.id))
      .sort((a, b) => {
        // Sort by start time
        const timeA = a.period.startTime.split(':').map(Number);
        const timeB = b.period.startTime.split(':').map(Number);
        return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
      });

    // Validate that periods are consecutive
    for (let i = 0; i < selectedAssignments.length - 1; i++) {
      const current = selectedAssignments[i];
      const next = selectedAssignments[i + 1];
      
      if (current.period.endTime !== next.period.startTime) {
        alert('Selected periods must be consecutive (no gaps between them)');
        return;
      }
    }

    // Create merged period
    const firstPeriod = selectedAssignments[0];
    const lastPeriod = selectedAssignments[selectedAssignments.length - 1];
    
    // Get subject name from first period (or use a default)
    const subjectName = selectedAssignments[0].subject || 'Merged Session';
    
    const mergedAssignment = {
      id: `merged-${Date.now()}`,
      period: {
        id: `merged-period-${Date.now()}`,
        name: subjectName, // Use subject name instead of generic "Merged Period"
        startTime: firstPeriod.period.startTime,
        endTime: lastPeriod.period.endTime,
        type: 'lesson',
        color: firstPeriod.period.color || '#3B82F6',
        isMerged: true, // Mark as merged so we can unmerge later
        originalPeriods: selectedAssignments.map(a => a.period) // Store original periods for unmerge
      },
      subject: firstPeriod.subject || '',
      room: firstPeriod.room || '',
      teacher: firstPeriod.teacher || ''
    };

    // Update timetable data: remove selected periods and add merged one
    setEditTimetableData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: [
          ...prev.days[day].filter(a => !selectedIds.includes(a.id)),
          mergedAssignment
        ].sort((a, b) => {
          // Re-sort by start time
          const timeA = a.period.startTime.split(':').map(Number);
          const timeB = b.period.startTime.split(':').map(Number);
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        })
      }
    }));

    // Clear selection
    setSelectedPeriodsForMerge(prev => ({
      ...prev,
      [day]: []
    }));

    alert(`✅ Successfully merged ${selectedIds.length} periods into one!`);
  };

  // ✅ NEW: Unmerge a merged period back into original periods
  const unmergePeriod = (day, mergedAssignmentId) => {
    setEditTimetableData(prev => {
      // Safety check
      if (!prev || !prev.days || !prev.days[day]) {
        alert('⚠️ Timetable data not found.');
        return prev;
      }

      // Find the merged period
      const mergedAssignment = prev.days[day].find(a => a.id === mergedAssignmentId);
      if (!mergedAssignment || !mergedAssignment.period.isMerged) {
        alert('⚠️ This period is not merged or cannot be unmerged.');
        return prev;
      }
      
      // Restore original periods
      const originalPeriods = mergedAssignment.period.originalPeriods;
      if (!originalPeriods || originalPeriods.length === 0) {
        alert('⚠️ No original periods found to restore.');
        return prev;
      }
      
      // Create assignments from original periods
      const restoredAssignments = originalPeriods.map((period, idx) => ({
        id: `restored-${Date.now()}-${idx}`,
        period: {
          ...period,
          isMerged: false, // Remove merged flag
          originalPeriods: undefined // Clear original periods
        },
        subject: mergedAssignment.subject || '',
        room: mergedAssignment.room || '',
        teacher: mergedAssignment.teacher || ''
      }));
      
      // Remove merged period and add restored periods
      const updatedAssignments = prev.days[day]
        .filter(a => a.id !== mergedAssignmentId)
        .concat(restoredAssignments)
        .sort((a, b) => {
          const timeA = a.period.startTime.split(':').map(Number);
          const timeB = b.period.startTime.split(':').map(Number);
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        });
      
      alert(`✅ Successfully unmerged period into ${originalPeriods.length} original periods!`);
      
      return {
        ...prev,
        days: {
          ...prev.days,
          [day]: updatedAssignments
        }
      };
    });
  };

  // ✅ NEW: Clear all selections for a day
  const clearPeriodSelection = (day) => {
    setSelectedPeriodsForMerge(prev => ({
      ...prev,
      [day]: []
    }));
  };

  // ✅ NEW: Duplicate timetable function
  const handleDuplicateTimetable = async (originalTimetable) => {
    // Prompt user for new timetable name
    const newName = prompt(
      'Enter a name for the duplicated timetable:', 
      `${originalTimetable.name} (Copy)`
    );
    
    if (!newName || !newName.trim()) {
      return; // User cancelled or entered empty name
    }
    
    // Prompt for new description (optional)
    const newDescription = prompt(
      'Enter a description for the duplicated timetable (optional):', 
      `Copy of ${originalTimetable.description || originalTimetable.name}`
    );
    
    try {
      console.log('🔄 Duplicating timetable:', originalTimetable.name);
      
      // Create a copy of the timetable data
      const duplicateData = {
        name: newName.trim(),
        description: newDescription?.trim() || `Copy of ${originalTimetable.name}`,
        assignedStudents: [], // Start with no students assigned to avoid conflicts
        formId: formId,
        createdBy: localStorage.getItem('teacherEmail') || 'Unknown Teacher',
        type: originalTimetable.type || 'weekly',
        periods: originalTimetable.periods || templateConfig.periods || [],
        days: originalTimetable.days || {}
      };
      
      // Choose the correct endpoint based on timetable type
      const endpoint = originalTimetable.type === 'weekly' 
        ? `${server}/create-weekly-timetable`
        : `${server}/create-timetable`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(duplicateData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Timetable duplicated successfully:', result);
        
        // Refresh the timetables list
        const refreshResponse = await fetch(`${server}/timetables/${formId}`);
        const refreshData = await refreshResponse.json();
        setTimetables(refreshData.timetables || []);
        
        alert(`Timetable "${newName}" created successfully as a copy!`);
      } else {
        const errorData = await response.text();
        console.error('❌ Failed to duplicate timetable:', errorData);
        alert(`Failed to duplicate timetable: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Error duplicating timetable:', error);
      alert(`Failed to duplicate timetable: ${error.message}`);
    }
  };

  return (
    <>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">🕐 Weekly Timetable Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => {
              reloadGlobalTemplate(); // Reload latest template from backend
              setShowTemplateEditor(true);
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ⚙️ Edit Template
          </button>
          <button
            onClick={openWeeklyEditor}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Create Weekly Timetable
          </button>
        </div>
      </div>

      {/* Template Configuration Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">📋 Current Template Settings</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-blue-700 font-medium">Start Time:</span>
            <br />
            <span className="text-blue-900">
              {String(templateConfig.startTime.hours).padStart(2, '0')}:
              {String(templateConfig.startTime.minutes).padStart(2, '0')}
            </span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">End Time:</span>
            <br />
            <span className="text-blue-900">
              {String(templateConfig.endTime.hours).padStart(2, '0')}:
              {String(templateConfig.endTime.minutes).padStart(2, '0')}
            </span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Lesson Duration:</span>
            <br />
            <span className="text-blue-900">{templateConfig.lessonDuration} min</span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Break Duration:</span>
            <br />
            <span className="text-blue-900">{templateConfig.breakDuration} min</span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Include Breaks:</span>
            <br />
            <span className="text-blue-900">{templateConfig.includeBreaks ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Existing Timetables */}
      <div className="space-y-8">
        {loadingTimetables ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading timetables...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Weekly Timetables */}
            {timetables.filter(t => t.type === 'weekly').length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">📅 Weekly Timetables</h3>
                {timetables.filter(t => t.type === 'weekly').map((timetable, index) => (
              <div key={`weekly-${index}`} className="bg-white p-6 rounded-lg shadow-md border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      📅 {timetable.name} <span className="text-sm text-blue-600">(Weekly)</span>
                    </h3>
                    <p className="text-gray-600 text-sm">{timetable.description}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      🗓️ Sunday to Thursday | Assigned to {timetable.assignedStudents?.length || 0} students
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedTimetable(selectedTimetable === `weekly-${index}` ? null : `weekly-${index}`)}
                      className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200"
                    >
                      {selectedTimetable === `weekly-${index}` ? 'Hide' : 'View'}
                    </button>
                    <button
                      onClick={() => handleEditTimetable(timetable)}
                      className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded text-sm hover:bg-yellow-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicateTimetable(timetable)}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => handleDeleteTimetable(timetable.id)}
                      className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {selectedTimetable === `weekly-${index}` && (
                  <div className="mt-4">
                    {/* Show Edit Interface if editing this timetable */}
                    {editingTimetable === timetable.id && editTimetableData ? (
                      <div className="space-y-4">
                        {/* Edit Mode Header */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-yellow-800">✏️ Editing: {editTimetableData.name}</h3>
                              <p className="text-sm text-yellow-600">Click 🗑️ to remove periods, or edit subject/room assignments</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={saveEditedTimetable}
                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                              >
                                ✅ Save Changes
                              </button>
                              <button
                                onClick={cancelEditTimetable}
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                              >
                                ❌ Cancel
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Editable Weekly Timetable */}
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => (
                            <div key={day} className="bg-gray-50 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3 text-center">{day}</h4>
                              
                              {/* Merge Controls */}
                              {editTimetableData.days?.[day]?.length > 1 && (
                                <div className="mb-3 space-y-2">
                                  <button
                                    onClick={() => mergeSelectedPeriods(day)}
                                    disabled={!selectedPeriodsForMerge[day] || selectedPeriodsForMerge[day].length < 2}
                                    className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                      selectedPeriodsForMerge[day] && selectedPeriodsForMerge[day].length >= 2
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                  >
                                    🔗 Merge Selected ({selectedPeriodsForMerge[day]?.length || 0})
                                  </button>
                                  {selectedPeriodsForMerge[day] && selectedPeriodsForMerge[day].length > 0 && (
                                    <button
                                      onClick={() => clearPeriodSelection(day)}
                                      className="w-full bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs hover:bg-gray-300 transition-colors"
                                    >
                                      Clear Selection
                                    </button>
                                  )}
                                </div>
                              )}
                              
                              <div className="space-y-2">
                                {editTimetableData.days?.[day]?.map((assignment, assignmentIndex) => {
                                  const isSelected = selectedPeriodsForMerge[day]?.includes(assignment.id);
                                  
                                  return (
                                    <div 
                                      key={assignmentIndex}
                                      className={`bg-white rounded-lg border p-3 space-y-2 transition-all ${
                                        isSelected ? 'ring-2 ring-blue-500 border-blue-500' : ''
                                      }`}
                                    >
                                      {/* Selection Checkbox */}
                                      <div className="flex items-center gap-2 mb-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => togglePeriodSelection(day, assignment.id)}
                                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <label className="text-xs text-gray-600 font-medium">
                                          Select to merge
                                        </label>
                                      </div>
                                      
                                      {/* Period Info */}
                                      <div 
                                        className="p-2 rounded text-white text-sm text-center font-medium"
                                        style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                                      >
                                        {assignment.subject || assignment.period?.name} 
                                        <div className="text-xs opacity-90">
                                          {assignment.period?.startTime} - {assignment.period?.endTime}
                                        </div>
                                      </div>

                                      {/* Editable Fields - Show for all periods except breaks */}
                                      {assignment.period?.type !== 'break' && (
                                        <div className="space-y-2">
                                        {/* Subject Dropdown */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                                          <select
                                            value={assignment.subject || ''}
                                            onChange={(e) => updateAssignmentInEditTimetable(day, assignment.id, 'subject', e.target.value)}
                                            className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                          >
                                            <option value="">Select Subject</option>
                                            {adminSubjects.map(subject => (
                                              <option key={subject.id} value={subject.name}>
                                                {subject.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        {/* Room Dropdown */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Room</label>
                                          <select
                                            value={assignment.room || ''}
                                            onChange={(e) => updateAssignmentInEditTimetable(day, assignment.id, 'room', e.target.value)}
                                            className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                          >
                                            <option value="">Select Room</option>
                                            {adminRooms.map(room => (
                                              <option key={room.id} value={room.name}>
                                                {room.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        {/* Teacher Field */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Teacher</label>
                                          <select
                                            value={assignment.teacher || ''}
                                            onChange={(e) => updateAssignmentInEditTimetable(day, assignment.id, 'teacher', e.target.value)}
                                            className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                          >
                                            <option value="">Select teacher...</option>
                                            {staffData.map(staff => (
                                              <option key={staff.email} value={staff.email}>
                                                {staff.name} ({staff.email})
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                      )}

                                      {/* Unmerge Button (only show for merged periods) */}
                                      {assignment.period.isMerged && (
                                        <button
                                          onClick={() => unmergePeriod(day, assignment.id)}
                                          className="w-full bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-200 transition-colors mb-2"
                                        >
                                          ↩️ Unmerge Period
                                        </button>
                                      )}

                                      {/* Remove Period Button */}
                                      <button
                                        onClick={() => removePeriodFromEditTimetable(day, assignment.id)}
                                        className="w-full bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 transition-colors"
                                      >
                                        🗑️ Remove Period
                                      </button>
                                    </div>
                                  );
                                })}
                                {editTimetableData.days?.[day]?.length === 0 && (
                                  <div className="text-center py-4 text-gray-500 text-sm">
                                    No periods for {day}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Student Assignment Section */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-lg font-medium text-blue-800 mb-3">👥 Assign Students to Timetable</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-blue-200 rounded-lg p-3 bg-white">
                            {students.map(student => (
                              <label key={student.school_id} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editTimetableData.assignedStudents?.includes(student.school_id)}
                                  onChange={(e) => {
                                    const studentId = student.school_id;
                                    if (e.target.checked) {
                                      setEditTimetableData(prev => ({
                                        ...prev,
                                        assignedStudents: [...(prev.assignedStudents || []), studentId]
                                      }));
                                    } else {
                                      setEditTimetableData(prev => ({
                                        ...prev,
                                        assignedStudents: (prev.assignedStudents || []).filter(id => id !== studentId)
                                      }));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm">{student.full_name}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <p className="text-sm text-blue-700">
                              Selected: {editTimetableData.assignedStudents?.length || 0} students
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditTimetableData(prev => ({
                                    ...prev,
                                    assignedStudents: students.map(s => s.school_id)
                                  }));
                                }}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                              >
                                Select All
                              </button>
                              <button
                                onClick={() => {
                                  setEditTimetableData(prev => ({
                                    ...prev,
                                    assignedStudents: []
                                  }));
                                }}
                                className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Normal View Mode */
                      /* Weekly Timetable Display */
                      timetable.type === 'weekly' ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => (
                            <div key={day} className="bg-gray-50 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3 text-center">{day}</h4>
                              <div className="space-y-2">
                                {timetable.days?.[day]?.map((assignment, assignmentIndex) => (
                                  <div 
                                    key={assignmentIndex}
                                    className="p-3 rounded-lg text-white text-sm"
                                    style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                                  >
                                    <div className="flex justify-between items-start mb-1">
                                      <div className="font-medium">{assignment.period?.name}</div>
                                      <div className="text-xs font-mono bg-black bg-opacity-20 px-2 py-1 rounded">
                                        {assignment.period?.startTime} - {assignment.period?.endTime}
                                      </div>
                                    </div>
                                    {assignment.subject && (
                                      <div className="text-sm mt-1 font-medium">
                                        📚 {assignment.subject}
                                      </div>
                                    )}
                                    {assignment.teacher && (
                                      <div className="text-xs opacity-90">
                                        👨‍🏫 {assignment.teacher}
                                      </div>
                                    )}
                                    {assignment.room && (
                                      <div className="text-xs opacity-90">
                                        🏫 {assignment.room}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {(!timetable.days?.[day] || timetable.days[day].length === 0) && (
                                  <div className="text-center text-gray-400 py-8 text-sm">
                                    No periods scheduled
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* Daily Timetable Display */
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-gray-50 rounded-lg">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="px-4 py-3 text-left text-sm font-medium bg-blue-100 border-r">
                                <div className="flex items-center">
                                  <span>🕒</span>
                                  <span className="ml-2">Time Period</span>
                                </div>
                              </th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Subject</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Teacher</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Room</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timetable.slots?.map((slot, slotIndex) => (
                              <tr key={slotIndex} className={slot.type === 'break' ? 'bg-yellow-50' : 'bg-white'}>
                                <td className="px-4 py-3 text-sm font-mono font-bold bg-blue-50 border-r">
                                  <div className="flex items-center">
                                    <span className="text-blue-800">🕒</span>
                                    <span className="ml-2 text-blue-900">{slot.startTime} - {slot.endTime}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    slot.type === 'lesson' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {slot.type === 'lesson' ? '📚 Lesson' : '☕ Break'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm">{slot.subject}</td>
                                <td className="px-4 py-2 text-sm">{slot.teacher}</td>
                                <td className="px-4 py-2 text-sm">{slot.room}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}

                    {/* Assigned Students */}
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-700 mb-2">Assigned Students:</h4>
                      <div className="flex flex-wrap gap-2">
                        {timetable.assignedStudents?.map(studentId => {
                          const student = students.find(s => s.school_id === studentId);
                          return student ? (
                            <span key={studentId} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                              {student.full_name}
                            </span>
                          ) : null
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Daily Timetables */}
        {timetables.filter(t => t.type !== 'weekly').length > 0 && (
          <div className="space-y-4 mt-8">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">📋 Daily Timetables</h3>
            {timetables.filter(t => t.type !== 'weekly').map((timetable, index) => (
              <div key={`daily-${index}`} className="bg-white p-6 rounded-lg shadow-md border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      📋 {timetable.name} <span className="text-sm text-green-600">(Daily)</span>
                    </h3>
                    <p className="text-gray-600 text-sm">{timetable.description}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      🕐 {timetable.slots?.length || 0} time slots | Assigned to {timetable.assignedStudents?.length || 0} students
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedTimetable(selectedTimetable === `daily-${index}` ? null : `daily-${index}`)}
                      className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200"
                    >
                      {selectedTimetable === `daily-${index}` ? 'Hide' : 'View'}
                    </button>
                    <button
                      onClick={() => handleDuplicateTimetable(timetable)}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => handleDeleteTimetable(timetable.id)}
                      className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {selectedTimetable === `daily-${index}` && (
                  <div className="mt-4">
                    {/* Daily Timetable Display */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-gray-50 rounded-lg">
                        <thead>
                          <tr className="bg-gray-200">
                            <th className="px-4 py-2 text-left text-sm font-medium">Time</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Subject</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Teacher</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Room</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timetable.slots?.map((slot, slotIndex) => (
                            <tr key={slotIndex} className={slot.type === 'break' ? 'bg-yellow-50' : 'bg-white'}>
                              <td className="px-4 py-2 text-sm font-mono">
                                {slot.startTime} - {slot.endTime}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  slot.type === 'lesson' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {slot.type === 'lesson' ? '📚 Lesson' : '☕ Break'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm">{slot.subject}</td>
                              <td className="px-4 py-2 text-sm">{slot.teacher}</td>
                              <td className="px-4 py-2 text-sm">{slot.room}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Assigned Students */}
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-700 mb-2">Assigned Students:</h4>
                      <div className="flex flex-wrap gap-2">
                        {timetable.assignedStudents?.map(studentId => {
                          const student = students.find(s => s.school_id === studentId);
                          return student ? (
                            <span key={studentId} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                              {student.full_name}
                            </span>
                          ) : null
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">⚙️ Edit Timetable Template</h3>
              <button
                onClick={() => setShowTemplateEditor(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Template Mode Selector */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3 text-gray-800">📋 Template Mode</h4>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={templateMode === 'periods'}
                      onChange={() => switchToPeriodsMode()}
                      className="mr-2"
                    />
                    <span className="text-sm">🎯 Create Periods (Full Control)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={templateMode === 'auto'}
                      onChange={() => switchToAutoMode()}
                      className="mr-2"
                    />
                    <span className="text-sm">🤖 Auto Generate</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={templateMode === 'manual'}
                      onChange={() => switchToManualMode()}
                      className="mr-2"
                    />
                    <span className="text-sm">✋ Manual Slot Creation</span>
                  </label>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {templateMode === 'periods' 
                    ? '🎯 Create custom periods with full control, then build weekly timetables'
                    : templateMode === 'auto' 
                    ? '🔄 Automatically generate time slots based on duration settings'
                    : '🛠️ Create and customize each time slot individually'
                  }
                </p>
              </div>

              {/* Periods Mode - Full Control Period Editor */}
              {templateMode === 'periods' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-800">🎯 Create Your Periods</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={addNewPeriod}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        + Add Period (50min)
                      </button>
                      <button
                        onClick={addQuick15MinPeriod}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center gap-1"
                        title="Quickly add a 15-minute period following the previous session"
                      >
                        ⚡ Quick 15min
                      </button>
                      <button
                        onClick={() => setShowWeeklyEditor(true)}
                        disabled={!templateConfig.periods || templateConfig.periods.length === 0}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        📅 Create Weekly Timetable
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(templateConfig.periods || []).map((period, index) => (
                      <div key={period.id} className="p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="font-medium text-sm text-gray-800">
                            {period.type === 'break' ? '☕' : '📚'} {period.name}
                          </h5>
                          <button
                            onClick={() => removePeriod(period.id)}
                            className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Period Name</label>
                            <input
                              type="text"
                              value={period.name}
                              onChange={(e) => updatePeriod(period.id, 'name', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Period name"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                            <input
                              type="time"
                              value={period.startTime}
                              onChange={(e) => updatePeriod(period.id, 'startTime', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                            <input
                              type="time"
                              value={period.endTime}
                              onChange={(e) => updatePeriod(period.id, 'endTime', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={period.type}
                              onChange={(e) => updatePeriod(period.id, 'type', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="lesson">📚 Lesson</option>
                              <option value="break">☕ Break</option>
                              <option value="lunch">🍽️ Lunch</option>
                              <option value="assembly">🏛️ Assembly</option>
                              <option value="study">📖 Study Period</option>
                            </select>
                          </div>
                          {period.type === 'break' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                              <input
                                type="color"
                                value={period.color || '#F59E0B'}
                                onChange={(e) => updatePeriod(period.id, 'color', e.target.value)}
                                className="w-full h-8 border border-gray-300 rounded"
                              />
                            </div>
                          )}
                          {period.type === 'lesson' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                              <div className="w-full h-8 bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-xs text-gray-600">
                                Set by Subject
                              </div>
                            </div>
                          )}
                          <div className="flex items-end">
                            <div 
                              className="w-full h-8 rounded border-2 border-gray-300 flex items-center justify-center text-xs font-medium text-white"
                              style={{ backgroundColor: period.type === 'break' ? (period.color || '#F59E0B') : '#E5E7EB' }}
                            >
                              Preview
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(!templateConfig.periods || templateConfig.periods.length === 0) && (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <p className="text-lg">🎯 No periods created yet</p>
                        <p className="text-sm">Click "Add Period" to create your first period</p>
                      </div>
                    )}
                  </div>

                  {/* Periods Preview */}
                  {templateConfig.periods && templateConfig.periods.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-3">📊 Daily Schedule Preview</h4>
                      <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                        <div className="space-y-2">
                          {templateConfig.periods.map((period, index) => (
                            <div 
                              key={period.id} 
                              className="flex justify-between items-center p-2 rounded text-white text-sm font-medium"
                              style={{ backgroundColor: period.color }}
                            >
                              <span>{period.startTime} - {period.endTime}</span>
                              <span>
                                {period.type === 'lesson' ? '📚' : 
                                 period.type === 'break' ? '☕' : 
                                 period.type === 'lunch' ? '🍽️' : 
                                 period.type === 'assembly' ? '🏛️' : '📖'} {period.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Auto Mode Configuration */}
              {templateMode === 'auto' && (
                <div className="space-y-4">
                  {/* Time Range */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-800 mb-3">🕐 Time Range</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={templateConfig.startTime.hours}
                            onChange={(e) => updateTemplate({
                              ...templateConfig,
                              startTime: { ...templateConfig.startTime, hours: parseInt(e.target.value) }
                            })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <span className="self-center">:</span>
                          <select
                            value={templateConfig.startTime.minutes}
                            onChange={(e) => updateTemplate({
                              ...templateConfig,
                              startTime: { ...templateConfig.startTime, minutes: parseInt(e.target.value) }
                            })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(min => (
                              <option key={min} value={min}>{String(min).padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={templateConfig.endTime.hours}
                            onChange={(e) => updateTemplate({
                              ...templateConfig,
                              endTime: { ...templateConfig.endTime, hours: parseInt(e.target.value) }
                            })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <span className="self-center">:</span>
                          <select
                            value={templateConfig.endTime.minutes}
                            onChange={(e) => updateTemplate({
                              ...templateConfig,
                              endTime: { ...templateConfig.endTime, minutes: parseInt(e.target.value) }
                            })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(min => (
                              <option key={min} value={min}>{String(min).padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

              {/* Duration Settings */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">⏱️ Duration Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lesson Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="120"
                      step="5"
                      value={templateConfig.lessonDuration}
                      onChange={(e) => updateTemplate({
                        ...templateConfig,
                        lessonDuration: parseInt(e.target.value) || 50
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Break Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="60"
                      step="5"
                      value={templateConfig.breakDuration}
                      onChange={(e) => updateTemplate({
                        ...templateConfig,
                        breakDuration: parseInt(e.target.value) || 20
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!templateConfig.includeBreaks}
                    />
                  </div>
                </div>
              </div>

              {/* Break Settings */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">☕ Break Settings</h4>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateConfig.includeBreaks}
                    onChange={(e) => updateTemplate({
                      ...templateConfig,
                      includeBreaks: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className="text-sm">Include break periods between lessons</span>
                </label>
              </div>

                </div>
              )}

              {/* Manual Mode - Slot by Slot Editor */}
              {templateMode === 'manual' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-800">🛠️ Time Slots</h4>
                    <button
                      onClick={addNewSlot}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      + Add Slot
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(templateConfig.manualSlots || []).map((slot, index) => (
                      <div key={slot.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="font-medium text-sm text-gray-800">
                            Slot {index + 1} {slot.type === 'break' ? '☕' : '📚'}
                          </h5>
                          <button
                            onClick={() => removeSlot(slot.id)}
                            className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                            <input
                              type="time"
                              value={slot.startTime}
                              onChange={(e) => updateSlot(slot.id, 'startTime', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                            <input
                              type="time"
                              value={slot.endTime}
                              onChange={(e) => updateSlot(slot.id, 'endTime', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={slot.type}
                              onChange={(e) => updateSlot(slot.id, 'type', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="lesson">📚 Lesson</option>
                              <option value="break">☕ Break</option>
                              <option value="lunch">🍽️ Lunch</option>
                              <option value="assembly">🏛️ Assembly</option>
                              <option value="study">📖 Study Period</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                            <select
                              value={slot.subject}
                              onChange={(e) => updateSlot(slot.id, 'subject', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select subject...</option>
                              {adminSubjects.map(subject => (
                                <option key={subject.id} value={subject.name}>{subject.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Room</label>
                            <select
                              value={slot.room}
                              onChange={(e) => updateSlot(slot.id, 'room', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select room...</option>
                              {adminRooms.map(room => (
                                <option key={room.id} value={room.name}>{room.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(!templateConfig.manualSlots || templateConfig.manualSlots.length === 0) && (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <p className="text-lg">📝 No time slots created yet</p>
                        <p className="text-sm">Click "Add Slot" to create your first time slot</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Preview Section - Always Show */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">👀 Preview</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                  <div className="text-sm space-y-1">
                    {templateMode === 'periods' ? (
                      // Show periods with their custom colors
                      templateConfig.periods?.map((period, index) => (
                        <div 
                          key={period.id} 
                          className="flex justify-between p-2 rounded text-white text-sm font-medium"
                          style={{ backgroundColor: period.type === 'break' ? (period.color || '#F59E0B') : '#E5E7EB' }}
                        >
                          <span className="font-mono">{period.startTime} - {period.endTime}</span>
                          <span>
                            {period.type === 'lesson' ? '📚' : 
                             period.type === 'break' ? '☕' : 
                             period.type === 'lunch' ? '🍽️' : 
                             period.type === 'assembly' ? '🏛️' : '📖'} {period.name}
                          </span>
                        </div>
                      ))
                    ) : (
                      // Show auto/manual generated slots
                      generateDefaultTimeSlots(templateConfig).map((slot, index) => (
                        <div key={index} className={`flex justify-between p-1 rounded ${
                          slot.type === 'break' ? 'bg-yellow-100' : 
                          slot.type === 'lunch' ? 'bg-green-100' :
                          slot.type === 'assembly' ? 'bg-purple-100' :
                          'bg-blue-100'
                        }`}>
                          <span className="font-mono">{slot.startTime} - {slot.endTime}</span>
                          <span className="text-xs">
                            {slot.type === 'lesson' ? '📚' : 
                             slot.type === 'break' ? '☕' : 
                             slot.type === 'lunch' ? '🍽️' : 
                             slot.type === 'assembly' ? '🏛️' : '📖'} {slot.subject || slot.type.charAt(0).toUpperCase() + slot.type.slice(1)}
                            {slot.room && ` (${slot.room})`}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  {templateMode === 'periods' && (!templateConfig.periods || templateConfig.periods.length === 0) && (
                    <p className="text-gray-500 text-sm">No periods created yet</p>
                  )}
                  {templateMode !== 'periods' && generateDefaultTimeSlots(templateConfig).length === 0 && (
                    <p className="text-gray-500 text-sm">No time slots generated</p>
                  )}
                </div>
              </div>

              {/* Preset Templates - Only in Auto Mode */}
              {templateMode === 'auto' && (
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-3">📋 Quick Templates</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <button
                      onClick={() => updateTemplate({
                        startTime: { hours: 8, minutes: 0 },
                        endTime: { hours: 15, minutes: 0 },
                        lessonDuration: 45,
                        breakDuration: 15,
                        includeBreaks: true
                      })}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                    >
                      🏫 Standard School<br />
                      <span className="text-xs text-gray-600">8:00 AM - 3:00 PM, 45min lessons</span>
                    </button>
                    <button
                      onClick={() => updateTemplate({
                        startTime: { hours: 9, minutes: 0 },
                        endTime: { hours: 12, minutes: 0 },
                        lessonDuration: 60,
                        breakDuration: 15,
                        includeBreaks: true
                      })}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                    >
                      🌅 Morning Only<br />
                      <span className="text-xs text-gray-600">9:00 AM - 12:00 PM, 60min lessons</span>
                    </button>
                    <button
                      onClick={resetToDefaultTemplate}
                      className="p-2 bg-blue-100 hover:bg-blue-200 rounded text-sm"
                    >
                      🔄 Current School<br />
                      <span className="text-xs text-gray-600">7:10 AM - 1:45 PM, 50min lessons</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    const newSlots = generateDefaultTimeSlots(templateConfig);
                    setNewTimetable(prev => ({ ...prev, slots: newSlots }));
                    setShowTemplateEditor(false);
                  }}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  ✅ Apply Template
                </button>
                <button
                  onClick={() => setShowTemplateEditor(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Weekly Timetable Editor Modal */}
      {showWeeklyEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">📅 Create Weekly Timetable</h3>
              <button
                onClick={() => setShowWeeklyEditor(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weekly Timetable Name *
                  </label>
                  <input
                    type="text"
                    value={weeklyTimetable.name}
                    onChange={(e) => setWeeklyTimetable(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 5C Weekly Schedule, Science Stream Timetable"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={weeklyTimetable.description}
                    onChange={(e) => setWeeklyTimetable(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of this weekly timetable"
                  />
                </div>
              </div>

              {/* Available Periods */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-medium text-gray-800">📚 Available Periods</h4>
                  <button
                    onClick={() => {
                      console.log('🔄 Manual auto-fill triggered');
                      autoPopulateWeeklyTimetable();
                    }}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                  >
                    🔄 Auto-fill All Days
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 bg-gray-50 rounded-lg">
                  {templateConfig.periods?.map(period => (
                    <div
                      key={period.id}
                      className="p-2 rounded text-white text-xs font-medium text-center cursor-move"
                      style={{ backgroundColor: period.type === 'break' ? (period.color || '#F59E0B') : '#3B82F6' }}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify(period));
                      }}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {period.type === 'lesson' ? '📚' : 
                         period.type === 'break' ? '☕' : 
                         period.type === 'lunch' ? '🍽️' : 
                         period.type === 'assembly' ? '🏛️' : '📖'} 
                        <span>{period.name}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <span className="text-xs">🕐</span>
                        <span className="text-xs font-bold bg-white bg-opacity-25 px-1 py-0.5 rounded">
                          {period.startTime}-{period.endTime}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!templateConfig.periods || templateConfig.periods.length === 0) && (
                    <div className="col-span-full text-center py-4 text-gray-500">
                      <p className="text-sm">⚠️ No template periods available</p>
                      <p className="text-xs">Please create periods in the template first</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  📋 Drag periods to the day columns below, or use "Auto-fill All Days" to populate automatically
                </p>
              </div>

              {/* Weekly Schedule Grid */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">📅 Weekly Schedule</h4>
                <div className="grid grid-cols-5 gap-4">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => (
                    <div key={day} className="border border-gray-300 rounded-lg p-3 bg-white">
                      <h5 className="font-medium text-center mb-2 text-gray-800 border-b pb-2">{day}</h5>
                      
                      {/* Merge Controls */}
                      {weeklyTimetable.days[day].length > 0 && (
                        <div className="mb-2 flex gap-1">
                          <button
                            onClick={() => mergeWeeklyPeriods(day)}
                            disabled={!weeklyMergeSelections[day] || weeklyMergeSelections[day].length < 2}
                            className={`flex-1 px-2 py-1 rounded text-xs transition-colors ${
                              weeklyMergeSelections[day] && weeklyMergeSelections[day].length >= 2
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            🔗 Merge ({weeklyMergeSelections[day]?.length || 0})
                          </button>
                          {weeklyMergeSelections[day] && weeklyMergeSelections[day].length > 0 && (
                            <button
                              onClick={() => setWeeklyMergeSelections(prev => ({ ...prev, [day]: [] }))}
                              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Drop Zone */}
                      <div
                        className="min-h-96 space-y-2"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const periodData = JSON.parse(e.dataTransfer.getData('application/json'));
                          assignPeriodToDay(day, periodData.id);
                        }}
                      >
                        {weeklyTimetable.days[day].map((assignment, index) => (
                          <div
                            key={assignment.id}
                            className="p-2 rounded text-white text-xs relative group"
                            style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                          >
                            {/* Checkbox for merge selection */}
                            {!assignment.period?.isMerged && (
                              <input
                                type="checkbox"
                                checked={weeklyMergeSelections[day]?.includes(assignment.id)}
                                onChange={(e) => {
                                  setWeeklyMergeSelections(prev => ({
                                    ...prev,
                                    [day]: e.target.checked
                                      ? [...(prev[day] || []), assignment.id]
                                      : (prev[day] || []).filter(id => id !== assignment.id)
                                  }));
                                }}
                                className="absolute top-1 left-1 w-3 h-3 cursor-pointer"
                              />
                            )}
                            
                            {/* Unmerge button for merged periods */}
                            {assignment.period?.isMerged && (
                              <button
                                onClick={() => unmergeWeeklyPeriod(day, assignment.id)}
                                className="absolute top-1 left-1 bg-yellow-500 text-white rounded px-1 text-xs hover:bg-yellow-600"
                                title="Unmerge this period"
                              >
                                ↩️
                              </button>
                            )}
                            
                            <button
                              onClick={() => removePeriodFromDay(day, assignment.id)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                            
                            <div className="font-medium">
                              {assignment.subject || assignment.period.name}
                            </div>
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs">🕐</span>
                              <span className="text-xs font-semibold bg-white bg-opacity-20 px-1.5 py-0.5 rounded">
                                {assignment.period.startTime}-{assignment.period.endTime}
                              </span>
                            </div>
                            
                            {/* Subject Input */}
                            <select
                              value={assignment.subject}
                              onChange={(e) => updateDayAssignment(day, assignment.id, 'subject', e.target.value)}
                              className="w-full mt-1 px-1 py-0.5 text-xs bg-white bg-opacity-20 border border-white border-opacity-30 rounded text-white"
                            >
                              <option value="" style={{color: '#000'}}>Select subject...</option>
                              {adminSubjects.map(subject => (
                                <option key={subject.id} value={subject.name} style={{color: '#000'}}>{subject.name}</option>
                              ))}
                            </select>
                            
                            {/* Teacher Dropdown */}
                            <select
                              value={assignment.teacher}
                              onChange={(e) => updateDayAssignment(day, assignment.id, 'teacher', e.target.value)}
                              className="w-full mt-1 px-1 py-0.5 text-xs bg-white bg-opacity-20 border border-white border-opacity-30 rounded text-white"
                            >
                              <option value="" style={{color: '#000'}}>Select teacher...</option>
                              {staffData.map(staff => (
                                <option key={staff.email} value={staff.email} style={{color: '#000'}}>
                                  {staff.name} ({staff.email})
                                </option>
                              ))}
                            </select>
                            
                            {/* Room Input */}
                            <select
                              value={assignment.room}
                              onChange={(e) => updateDayAssignment(day, assignment.id, 'room', e.target.value)}
                              className="w-full mt-1 px-1 py-0.5 text-xs bg-white bg-opacity-20 border border-white border-opacity-30 rounded text-white"
                            >
                              <option value="" style={{color: '#000'}}>Select room...</option>
                              {adminRooms.map(room => (
                                <option key={room.id} value={room.name} style={{color: '#000'}}>{room.name}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                        
                        {weeklyTimetable.days[day].length === 0 && (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400">
                            <p className="text-sm">Drop periods here</p>
                            <p className="text-xs">Drag from available periods above</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Assignment */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">👥 Assign to Students</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {students.map(student => (
                    <label key={student.school_id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={weeklyTimetable.assignedStudents.includes(student.school_id)}
                        onChange={() => {
                          setWeeklyTimetable(prev => ({
                            ...prev,
                            assignedStudents: prev.assignedStudents.includes(student.school_id)
                              ? prev.assignedStudents.filter(id => id !== student.school_id)
                              : [...prev.assignedStudents, student.school_id]
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{student.full_name}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Selected: {weeklyTimetable.assignedStudents.length} students
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setWeeklyTimetable(prev => ({
                          ...prev,
                          assignedStudents: students.map(s => s.school_id)
                        }));
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => {
                        setWeeklyTimetable(prev => ({
                          ...prev,
                          assignedStudents: []
                        }));
                      }}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={async () => {
                    if (!weeklyTimetable.name.trim()) {
                      alert('Please enter a timetable name');
                      return;
                    }
                    
                    try {
                      const payload = {
                        ...weeklyTimetable,
                        formId,
                        type: 'weekly',
                        periods: templateConfig.periods,
                        createdBy: localStorage.getItem('teacherEmail') || 'Unknown Teacher'
                      };
                      
                      const response = await fetch(`${server}/create-weekly-timetable`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        alert('Weekly timetable created successfully!');
                        setWeeklyTimetable({
                          name: '',
                          description: '',
                          assignedStudents: [],
                          days: {
                            Monday: [],
                            Tuesday: [],
                            Wednesday: [],
                            Thursday: [],
                            Friday: []
                          }
                        });
                        setShowWeeklyEditor(false);
                        // Refresh timetables list
                        fetch(`${server}/timetables/${formId}`)
                          .then(res => res.json())
                          .then(data => setTimetables(data.timetables || []))
                          .catch(err => console.error("Failed to load timetables:", err));
                      } else {
                        const errorData = await response.text();
                        alert(`Failed to create weekly timetable: ${response.status} - ${errorData}`);
                      }
                    } catch (error) {
                      console.error('Error creating weekly timetable:', error);
                      alert(`Failed to create weekly timetable: ${error.message}`);
                    }
                  }}
                  disabled={!weeklyTimetable.name.trim()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  📅 Create Weekly Timetable
                </button>
                <button
                  onClick={() => setShowWeeklyEditor(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

// ✅ NEW: Attendance Tab Component
const AttendanceTab = ({ formId, students }) => {
  const server = buildApiUrl("");  // ✅ Use config instead of localhost
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [presentCodes, setPresentCodes] = useState([]);
  const [absenceCodes, setAbsenceCodes] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [todayRecords, setTodayRecords] = useState(null);
  const [loadingIsams, setLoadingIsams] = useState(false);
  const [outOfSchoolModal, setOutOfSchoolModal] = useState({ isOpen: false, student: null, reason: '' });

  // Load present codes, absence codes and current period on mount
  useEffect(() => {
    const loadAttendanceData = async () => {
      try {
        // Load present codes
        const codesRes = await fetch(`${server}/attendance/present-codes`);
        const codesData = await codesRes.json();
        if (codesData.success) {
          setPresentCodes(codesData.codes);
        }

        // Load absence codes
        const absenceRes = await fetch(`${server}/attendance/absence-codes`);
        const absenceData = await absenceRes.json();
        if (absenceData.success) {
          setAbsenceCodes(absenceData.codes);
        }

        // Load current period
        const periodRes = await fetch(`${server}/attendance/current-period`);
        const periodData = await periodRes.json();
        if (periodData.success) {
          setCurrentPeriod(periodData);
        }

        // Load iSAMS attendance data first
        setLoadingIsams(true);
        try {
          console.log(`📡 Fetching iSAMS attendance for form ${formId}...`);
          const isamsRes = await fetch(`${server}/attendance/isams-form/${formId}`);
          const isamsData = await isamsRes.json();
          
          console.log(`📊 iSAMS API Response for ${formId}:`, isamsData);
          
          if (isamsData.success && isamsData.students && isamsData.students.length > 0) {
            console.log(`✅ Loaded ${isamsData.students.length} students from iSAMS`);
            
            // Pre-populate with iSAMS data
            const isamsRecords = {};
            isamsData.students.forEach(student => {
              const pupilId = student.pupilId || student.studentId;
              isamsRecords[pupilId] = {
                status: student.status, // 'present', 'late', 'absent'
                absentCodeId: student.absenceCodeId || null,
                minutesLate: student.isLate ? (student.numberOfMinutesLate || 0) : 0,
                comment: '',
                source: 'isams'
              };
            });
            setAttendanceRecords(isamsRecords);
          } else {
            console.log(`ℹ️ No iSAMS data found for ${formId}`);
          }
        } catch (error) {
          console.error(`❌ Error fetching iSAMS attendance:`, error);
        } finally {
          setLoadingIsams(false);
        }

        // Load today's existing records if any (will override iSAMS if exists)
        const recordsRes = await fetch(`${server}/attendance/records/${formId}`);
        const recordsData = await recordsRes.json();
        if (recordsData.success && recordsData.records.length > 0) {
          setTodayRecords(recordsData);
          
          // Pre-populate the form with existing records (takes priority over iSAMS)
          const existingRecords = {};
          recordsData.records.forEach(record => {
            existingRecords[record.studentId] = {
              status: record.status,
              absentCodeId: record.absentCodeId,
              minutesLate: record.minutesLate || 0,
              comment: record.comment || '',
              source: 'database'
            };
          });
          setAttendanceRecords(existingRecords);
        }

      } catch (error) {
        console.error('Error loading attendance data:', error);
      }
    };

    if (formId) {
      loadAttendanceData();
    }
  }, [formId]);

  // Initialize attendance records for all students
  useEffect(() => {
    if (students.length > 0 && Object.keys(attendanceRecords).length === 0 && !todayRecords) {
      const initialRecords = {};
      students.forEach(student => {
        initialRecords[student.pupil_id] = {
          status: '', // No default selection
          absentCodeId: null,
          minutesLate: 0,
          comment: ''
        };
      });
      setAttendanceRecords(initialRecords);
    }
  }, [students, attendanceRecords, todayRecords]);

  const updateAttendanceRecord = (studentId, field, value) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const handleOutOfSchoolClick = (student) => {
    setOutOfSchoolModal({ isOpen: true, student, reason: '' });
  };

  const handleOutOfSchoolSubmit = async () => {
    if (!outOfSchoolModal.reason.trim()) {
      alert('Please enter a reason for out of school');
      return;
    }

    try {
      const response = await fetch(`${server}/attendance/out-of-school`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: outOfSchoolModal.student.school_id,
          formId: formId,
          reason: outOfSchoolModal.reason,
          date: new Date().toISOString().split('T')[0],
          teacherEmail: localStorage.getItem('teacherEmail') || 'Unknown Teacher'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Out of school recorded successfully');
        setOutOfSchoolModal({ isOpen: false, student: null, reason: '' });
      } else {
        alert(`Failed to record: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting out of school:', error);
      alert('Error submitting out of school');
    }
  };

  const handleSubmitAttendance = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare records for submission
      const records = students.map(student => ({
        studentId: student.pupil_id,
        ...attendanceRecords[student.pupil_id]
      }));

      const payload = {
        records,
        formId,
        teacherEmail: localStorage.getItem('teacherEmail') || 'Unknown Teacher'
      };

      console.log('Submitting attendance:', payload);

      const response = await fetch(`${server}/attendance/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: `Successfully submitted attendance for ${result.successful} students`,
          details: result
        });
        
        // Reload today's records
        setTimeout(async () => {
          const recordsRes = await fetch(`${server}/attendance/records/${formId}`);
          const recordsData = await recordsRes.json();
          if (recordsData.success) {
            setTodayRecords(recordsData);
          }
        }, 1000);
        
      } else {
        setSubmitStatus({
          type: 'error',
          message: `Failed to submit attendance: ${result.error}`,
          details: result
        });
      }

    } catch (error) {
      console.error('Error submitting attendance:', error);
      setSubmitStatus({
        type: 'error',
        message: `Error submitting attendance: ${error.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAbsentCodes = () => {
    // Return only active absence codes that are commonly used for student absences
    return absenceCodes.filter(code => 
      code.isOOSActive && 
      !['P', 'L', 'U', 'Q'].includes(code.code) // Exclude present/late codes
    ).sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-800';
      case 'late': return 'bg-yellow-100 text-yellow-800';
      case 'absent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!students.length) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-lg">No students found for this form</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AM Attendance - Form {formId}</h2>
            <p className="text-gray-600">
              {currentPeriod ? `Registration Period: ${currentPeriod.period_id} | ${currentPeriod.date}` : 'Loading period info...'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Total Students</div>
            <div className="text-2xl font-bold text-blue-600">{students.length}</div>
          </div>
        </div>

        {/* Status Messages */}
        {submitStatus && (
          <div className={`p-4 rounded-lg mb-4 ${
            submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-medium">{submitStatus.message}</div>
            {submitStatus.details && submitStatus.details.failed_submissions?.length > 0 && (
              <div className="mt-2 text-sm">
                <div className="font-medium">Failed submissions:</div>
                <ul className="list-disc list-inside">
                  {submitStatus.details.failed_submissions.map((failure, idx) => (
                    <li key={idx}>{failure.studentId}: {failure.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Today's existing records notification */}
        {todayRecords && todayRecords.source !== 'isams' && (
          <div className="bg-blue-100 text-blue-800 p-4 rounded-lg mb-4">
            <div className="font-medium">📋 Attendance already submitted today</div>
            <div className="text-sm mt-1">
              Submitted by {todayRecords.teacher_email} at {new Date(todayRecords.submitted_at).toLocaleTimeString()}
              <br />
              You can modify and resubmit if needed.
            </div>
          </div>
        )}

        {/* iSAMS Loading notification */}
        {loadingIsams && (
          <div className="bg-purple-100 text-purple-800 p-4 rounded-lg mb-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            <div>
              <div className="font-medium">Loading attendance from iSAMS...</div>
              <div className="text-sm">Fetching today's registered attendance</div>
            </div>
          </div>
        )}

        {/* iSAMS Data loaded notification */}
        {!loadingIsams && attendanceRecords && Object.values(attendanceRecords).some(r => r.source === 'isams') && !todayRecords && (
          <div className="bg-purple-100 text-purple-800 p-4 rounded-lg mb-4">
            <div className="font-medium">✨ Attendance loaded from iSAMS</div>
            <div className="text-sm mt-1">
              Showing attendance already registered in iSAMS. You can modify and submit updates if needed.
            </div>
          </div>
        )}
      </div>

      {/* Attendance Form */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Student Attendance</h3>
            <button
              onClick={handleSubmitAttendance}
              disabled={isSubmitting}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Submitting...
                </>
              ) : (
                '📤 Submit Attendance'
              )}
            </button>
          </div>

          {/* Student List */}
          <div className="space-y-4">
            {students.map((student) => {
              const record = attendanceRecords[student.pupil_id] || {};
              
              return (
                <div key={student.pupil_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    {/* Student Info */}
                    <div className="flex items-center space-x-4">
                      {student.school_id ? (
                        <img 
                          src={`${server}/student-photo/${student.school_id}?v=3`}
                          alt={student.full_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-200 transition-transform duration-300 ease-in-out hover:scale-[3] hover:z-50 relative cursor-pointer"
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ease-in-out hover:scale-[3] hover:z-50 relative cursor-pointer ${student.school_id ? 'hidden' : ''}`}
                        style={student.school_id ? { display: 'none' } : {}}
                      >
                        <span className="text-blue-600 font-medium text-sm">
                          {student.full_name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{student.full_name}</div>
                        <div className="text-sm text-gray-500">ID: {student.school_id}</div>
                      </div>
                    </div>

                    {/* Attendance Controls */}
                    <div className="flex items-center space-x-4">
                      {/* Status Selection */}
                      <div className="flex space-x-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`status-${student.pupil_id}`}
                            value="present"
                            checked={record.status === 'present'}
                            onChange={(e) => updateAttendanceRecord(student.pupil_id, 'status', e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-green-600 font-medium">✅ Present</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`status-${student.pupil_id}`}
                            value="late"
                            checked={record.status === 'late'}
                            onChange={(e) => updateAttendanceRecord(student.pupil_id, 'status', e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-yellow-600 font-medium">⏱️ Late</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`status-${student.pupil_id}`}
                            value="absent"
                            checked={record.status === 'absent'}
                            onChange={(e) => updateAttendanceRecord(student.pupil_id, 'status', e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-red-600 font-medium">❌ Absent</span>
                        </label>
                        
                        {/* Out of School Button */}
                        <button
                          onClick={() => handleOutOfSchoolClick(student)}
                          className="flex items-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                          title="Out of School / Early Out"
                        >
                          <span className="text-xl mr-1">🚪</span>
                          <span className="text-sm font-medium">Out</span>
                        </button>
                      </div>

                      {/* Late Minutes (only show if late) */}
                      {record.status === 'late' && (
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-600">Minutes:</label>
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={record.minutesLate || 0}
                            onChange={(e) => updateAttendanceRecord(student.pupil_id, 'minutesLate', parseInt(e.target.value) || 0)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                          />
                        </div>
                      )}

                      {/* Absent Code (only show if absent) */}
                      {record.status === 'absent' && (
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-600">Reason:</label>
                          <select
                            value={record.absentCodeId || ''}
                            onChange={(e) => updateAttendanceRecord(student.pupil_id, 'absentCodeId', e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded text-sm max-w-48"
                          >
                            <option value="">Select reason</option>
                            {getAbsentCodes().map(code => (
                              <option key={code.id} value={code.id}>
                                [{code.code}] {code.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Comment field */}
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="Add a comment (optional)"
                      value={record.comment || ''}
                      onChange={(e) => updateAttendanceRecord(student.pupil_id, 'comment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Attendance Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {Object.values(attendanceRecords).filter(r => r.status === 'present' || (r.status === 'absent' && r.absentCodeId === 'B')).length}
            </div>
            <div className="text-sm text-gray-600">Present</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {Object.values(attendanceRecords).filter(r => r.status === 'late').length}
            </div>
            <div className="text-sm text-gray-600">Late</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {Object.values(attendanceRecords).filter(r => r.status === 'absent' && r.absentCodeId !== 'B').length}
            </div>
            <div className="text-sm text-gray-600">Absent</div>
          </div>
        </div>
      </div>

      {/* Out of School Modal */}
      {outOfSchoolModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">🚪 Out of School / Early Out</h3>
              <button
                onClick={() => setOutOfSchoolModal({ isOpen: false, student: null, reason: '' })}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-medium">
                    {outOfSchoolModal.student?.full_name?.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{outOfSchoolModal.student?.full_name}</div>
                  <div className="text-sm text-gray-500">ID: {outOfSchoolModal.student?.school_id}</div>
                </div>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for leaving school early:
              </label>
              <textarea
                value={outOfSchoolModal.reason}
                onChange={(e) => setOutOfSchoolModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Enter reason (e.g., Medical appointment, Family emergency, etc.)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setOutOfSchoolModal({ isOpen: false, student: null, reason: '' })}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleOutOfSchoolSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Submit to iSAMS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EnhancedMonitorTab = ({ 
  connectedStudents, 
  students, 
  formId, 
  screenPreviews, 
  setScreenPreviews, 
  remoteControlSessions, 
  setRemoteControlSessions, 
  fullscreenStudent, 
  setFullscreenStudent 
}) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pushContent, setPushContent] = useState('');
  const [showPushModal, setShowPushModal] = useState(false);
  const [controlAction, setControlAction] = useState('');

  // Function to check if a student is online
  const isStudentOnline = (student) => {
    return connectedStudents.some(connected => 
      connected.school_id === student.school_id || 
      connected.school_id === student.person_id
    );
  };

  // Function to get connected student data
  const getConnectedStudentData = (student) => {
    return connectedStudents.find(connected => 
      connected.school_id === student.school_id || 
      connected.school_id === student.person_id
    );
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Control functions
  const sendControlCommand = async (student, command) => {
    const connectedData = getConnectedStudentData(student);
    if (!connectedData) return;

    try {
      const ws = new WebSocket(`ws://${connectedData.ip}:8081`);
      
      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: "command",
          action: command.action,
          content: command.content || null
        }));
        setTimeout(() => ws.close(), 1000);
      };

      ws.onerror = (error) => {
        console.error('Control command failed:', error);
      };
    } catch (error) {
      console.error('Failed to send control command:', error);
    }
  };

  const blankScreen = (student) => {
    sendControlCommand(student, { action: 'blank_screen' });
    setControlAction(`Blanked ${student.full_name || student.name}'s screen`);
    setTimeout(() => setControlAction(''), 3000);
    
    // Update screen preview to show blank state instead of disconnecting
    const studentId = student.school_id || student.person_id;
    setScreenPreviews(prev => ({
      ...prev,
      [studentId]: null // Show as blank/loading state
    }));
  };

  const pushContentToStudent = (student, content) => {
    sendControlCommand(student, { action: 'push_content', content });
    setControlAction(`Pushed content to ${student.full_name || student.name}`);
    setTimeout(() => setControlAction(''), 3000);
  };

  const unblankScreen = (student) => {
    sendControlCommand(student, { action: 'unblank_screen' });
    setControlAction(`Unblanked ${student.full_name || student.name}'s screen`);
    setTimeout(() => setControlAction(''), 3000);
    
    // Restart screen preview after unblanking
    setTimeout(() => {
      const studentId = student.school_id || student.person_id;
      if (screenPreviews[studentId] === null) {
        startScreenPreview(student);
      }
    }, 1000);
  };

  const takeRemoteControl = (student) => {
    sendControlCommand(student, { action: 'enable_remote_control' });
    setControlAction(`Taking remote control of ${student.full_name || student.name}'s laptop`);
    setTimeout(() => setControlAction(''), 3000);
    
    // Open remote desktop window in fullscreen
    const connectedData = getConnectedStudentData(student);
    if (connectedData) {
      const remoteDesktopUrl = `http://${connectedData.ip}:8082`;
      // Open in fullscreen mode like AnyDesk
      const remoteWindow = window.open(
        remoteDesktopUrl, 
        `remote_${student.school_id}`,
        'width=' + screen.width + ',height=' + screen.height + ',fullscreen=yes,toolbar=no,menubar=no,scrollbars=no,resizable=yes,left=0,top=0'
      );
      
      // Request fullscreen when window loads
      if (remoteWindow) {
        remoteWindow.addEventListener('load', () => {
          try {
            remoteWindow.document.documentElement.requestFullscreen();
          } catch (e) {
            console.log('Fullscreen request failed:', e);
          }
        });
      }
    }
  };

  const releaseRemoteControl = (student) => {
    sendControlCommand(student, { action: 'disable_remote_control' });
    setControlAction(`Released remote control of ${student.full_name || student.name}'s laptop`);
    setTimeout(() => setControlAction(''), 3000);
  };

  // Screen preview functions - Enhanced with preview/HD modes
  const startScreenPreview = (student) => {
    const connectedData = getConnectedStudentData(student);
    if (!connectedData) return;

    const studentId = student.school_id || student.person_id;
    
    // Create WebSocket connection for screen preview
    const ws = new WebSocket(`ws://${connectedData.ip}:8081`);
    
    ws.onopen = () => {
      console.log(`Screen preview connected for ${student.full_name || student.name}`);
      // Start preview stream (low-res thumbnails)
      ws.send(JSON.stringify({ command: 'start_preview_stream' }));
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'screen_data' && data.mode === 'preview') {
          setScreenPreviews(prev => ({
            ...prev,
            [studentId]: data.data // Full data URI for preview thumbnails
          }));
        }
      } catch (e) {
        console.error('Screen preview error:', e);
      }
    };
    
    ws.onclose = () => {
      console.log(`Screen preview disconnected for ${student.full_name || student.name}`);
      setScreenPreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[studentId];
        return newPreviews;
      });
    };
    
    // Store the WebSocket for cleanup
    setRemoteControlSessions(prev => ({
      ...prev,
      [studentId]: ws
    }));
  };

  const stopScreenPreview = (student) => {
    const studentId = student.school_id || student.person_id;
    const ws = remoteControlSessions[studentId];
    
    if (ws) {
      ws.close();
      setRemoteControlSessions(prev => {
        const newSessions = { ...prev };
        delete newSessions[studentId];
        return newSessions;
      });
    }
    
    setScreenPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[studentId];
      return newPreviews;
    });
  };

  const openFullscreenRemoteControl = (student) => {
    setFullscreenStudent(student);
    
    // Stop preview and clean up any existing fullscreen connection
    stopScreenPreview(student);
    
    const studentId = student.school_id || student.person_id;
    
    // Clean up any existing fullscreen WebSocket for this student
    const existingWs = remoteControlSessions[`fullscreen_${studentId}`];
    if (existingWs) {
      console.log(`Cleaning up existing fullscreen connection for ${student.full_name || student.name}`);
      if (existingWs.readyState === WebSocket.OPEN) {
        existingWs.send(JSON.stringify({ command: 'stop_screen_stream' }));
        existingWs.send(JSON.stringify({ command: 'disable_remote_control' }));
      }
      existingWs.close();
    }
    
    const connectedData = getConnectedStudentData(student);
    if (!connectedData) return;
    
    // Wait a moment for cleanup, then create new connection
    setTimeout(() => {
      console.log(`Creating new HD connection for ${student.full_name || student.name}`);
      
      // Create fresh WebSocket connection for full HD remote control
      const ws = new WebSocket(`ws://${connectedData.ip}:8081`);
      
      ws.onopen = () => {
        console.log(`HD remote control connected for ${student.full_name || student.name}`);
        // Start HD stream for fullscreen control
        ws.send(JSON.stringify({ command: 'start_hd_stream' }));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'screen_data' && data.mode === 'hd') {
            // Store screen dimensions for accurate coordinate mapping
            ws.screenWidth = data.screen_width;
            ws.screenHeight = data.screen_height;
            
            setScreenPreviews(prev => ({
              ...prev,
              [`fullscreen_${studentId}`]: data.data
            }));
            
            // Draw to canvas for fullscreen display
            const canvas = canvasRef.current;
            if (canvas) {
              const ctx = canvas.getContext('2d');
              const img = new Image();
              
              img.onload = () => {
                // Resize canvas to match screen
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
                
                // Draw the image to fill the canvas
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              };
              
              img.src = data.data;
            }
          } else if (data.type === 'remote_control_enabled') {
            setControlAction(`Remote control active for ${student.full_name || student.name}`);
            setTimeout(() => setControlAction(''), 3000);
          }
        } catch (e) {
          console.error('HD remote control error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log(`HD remote control disconnected for ${student.full_name || student.name}`);
      };
      
      ws.onerror = (error) => {
        console.error(`HD remote control WebSocket error for ${student.full_name || student.name}:`, error);
      };
      
      // Store the WebSocket for full control
      setRemoteControlSessions(prev => ({
        ...prev,
        [`fullscreen_${studentId}`]: ws
      }));
    }, 200); // 200ms delay for cleanup
  };

  const closeFullscreenRemoteControl = () => {
    if (fullscreenStudent) {
      const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
      const ws = remoteControlSessions[`fullscreen_${studentId}`];
      
      if (ws) {
        console.log(`Closing fullscreen control for ${fullscreenStudent.full_name || fullscreenStudent.name}`);
        
        // Send commands to stop streaming and disable control
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ command: 'stop_screen_stream' }));
          ws.send(JSON.stringify({ command: 'disable_remote_control' }));
        }
        
        // Close the WebSocket
        ws.close();
      }
      
      // Clean up all related state
      setRemoteControlSessions(prev => {
        const newSessions = { ...prev };
        delete newSessions[`fullscreen_${studentId}`];
        return newSessions;
      });
      
      setScreenPreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[`fullscreen_${studentId}`];
        return newPreviews;
      });
      
      // Clear canvas
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      
      setFullscreenStudent(null);
      
      // Restart preview mode for the student
      setTimeout(() => {
        startScreenPreview(fullscreenStudent);
      }, 500);
    }
  };

  // Canvas reference and event handlers for fullscreen remote control
  const canvasRef = useRef(null);

  const handleCanvasClick = (e) => {
    if (!fullscreenStudent) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
    const ws = remoteControlSessions[`fullscreen_${studentId}`];
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Calculate click coordinates relative to canvas
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      
      // Send click with viewer dimensions for proper scaling on server
      ws.send(JSON.stringify({
        command: 'mouse_click',
        x: canvasX,
        y: canvasY,
        viewer_width: rect.width,
        viewer_height: rect.height,
        button: 'left'
      }));
      
      console.log(`🖱️ HD Click: ${canvasX},${canvasY} in ${rect.width}x${rect.height} viewer`);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!fullscreenStudent) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
    const ws = remoteControlSessions[`fullscreen_${studentId}`];
    
    const screenWidth = ws?.screenWidth || 1920;
    const screenHeight = ws?.screenHeight || 1080;
    
    const scaleX = screenWidth / rect.width;
    const scaleY = screenHeight / rect.height;
    
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        command: 'mouse_move',
        x: x,
        y: y
      }));
    }
  };

  const handleCanvasKeyDown = (e) => {
    if (!fullscreenStudent) return;
    
    e.preventDefault();
    
    const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
    const ws = remoteControlSessions[`fullscreen_${studentId}`];
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        command: 'key_press',
        key: e.key
      }));
    }
  };

  // Update canvas with the fullscreen image
  useEffect(() => {
    if (fullscreenStudent && canvasRef.current) {
      const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
      const imageData = screenPreviews[`fullscreen_${studentId}`];
      
      if (imageData) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          canvas.width = canvas.offsetWidth;
          canvas.height = canvas.offsetHeight;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        
        img.src = imageData;
      }
    }
  }, [fullscreenStudent, screenPreviews]);

  // Auto-start previews for connected students
  React.useEffect(() => {
    connectedStudents.forEach(connectedData => {
      const student = students.find(s => 
        s.school_id === connectedData.school_id || s.person_id === connectedData.school_id
      );
      if (student) {
        const studentId = student.school_id || student.person_id;
        if (!remoteControlSessions[studentId] && !screenPreviews[studentId]) {
          startScreenPreview(student);
        }
      }
    });
    
    // Cleanup disconnected students
    Object.keys(remoteControlSessions).forEach(studentId => {
      if (!studentId.startsWith('fullscreen_')) {
        const isConnected = connectedStudents.some(c => 
          c.school_id === studentId || c.person_id === studentId
        );
        if (!isConnected) {
          const ws = remoteControlSessions[studentId];
          if (ws) ws.close();
          setRemoteControlSessions(prev => {
            const newSessions = { ...prev };
            delete newSessions[studentId];
            return newSessions;
          });
          setScreenPreviews(prev => {
            const newPreviews = { ...prev };
            delete newPreviews[studentId];
            return newPreviews;
          });
        }
      }
    });
  }, [connectedStudents, students]);

  // Bulk actions
  const blankAllScreens = () => {
    connectedStudents.forEach(connectedData => {
      const student = students.find(s => 
        s.school_id === connectedData.school_id || s.person_id === connectedData.school_id
      );
      if (student) blankScreen(student);
    });
  };

  const unblankAllScreens = () => {
    connectedStudents.forEach(connectedData => {
      const student = students.find(s => 
        s.school_id === connectedData.school_id || s.person_id === connectedData.school_id
      );
      if (student) unblankScreen(student);
    });
  };

  const pushToAllStudents = () => {
    if (!pushContent.trim()) return;
    connectedStudents.forEach(connectedData => {
      const student = students.find(s => 
        s.school_id === connectedData.school_id || s.person_id === connectedData.school_id
      );
      if (student) pushContentToStudent(student, pushContent);
    });
    setShowPushModal(false);
    setPushContent('');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📺 Class Monitor - Form {formId}</h2>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>Total Students: {students.length}</span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            Online: {connectedStudents.length}
          </span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
            Offline: {students.length - connectedStudents.length}
          </span>
        </div>
      </div>

      {/* Control Panel */}
      <div className="mb-6 bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">🎮 Classroom Controls</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={blankAllScreens}
            disabled={connectedStudents.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            ⚫ Blank All Screens
          </button>
          <button
            onClick={unblankAllScreens}
            disabled={connectedStudents.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            ✅ Unblank All Screens
          </button>
          <button
            onClick={() => setShowPushModal(true)}
            disabled={connectedStudents.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            📤 Push Content to All
          </button>
          <button
            onClick={() => {
              connectedStudents.forEach(student => {
                // Send control command without opening windows
                sendControlCommand(student, { action: 'enable_remote_control' });
              });
              setControlAction('Taking remote control of all connected laptops');
              setTimeout(() => setControlAction(''), 3000);
            }}
            disabled={connectedStudents.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            🎮 Remote Control All
          </button>
        </div>
        {controlAction && (
          <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-yellow-800 text-sm">
            {controlAction}
          </div>
        )}
      </div>

      {/* Student Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {students.map((student, index) => {
          const isOnline = isStudentOnline(student);
          const connectedData = getConnectedStudentData(student);
          const studentName = student.full_name || student.name || `Student ${student.school_id}`;

          return (
            <div
              key={student.school_id || index}
              className={`relative rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                isOnline 
                  ? 'border-green-500 hover:border-green-600 shadow-lg hover:shadow-xl' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => setSelectedStudent(isOnline ? student : null)}
            >
              {/* Student Screen/Thumbnail */}
              <div className="relative aspect-video rounded-t-lg overflow-hidden bg-gray-900">
                {isOnline && connectedData && screenPreviews[student.school_id] ? (
                  <img
                    src={screenPreviews[student.school_id]}
                    alt={`${studentName}'s screen`}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => openFullscreenRemoteControl(student)}
                  />
                ) : isOnline && connectedData && screenPreviews[student.school_id] === null ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black">
                    <div className="text-center text-white">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="text-lg">⚫</span>
                      </div>
                      <div className="text-xs">Screen Blanked</div>
                    </div>
                  </div>
                ) : isOnline && connectedData ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-center text-white">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="text-lg">📡</span>
                      </div>
                      <div className="text-xs">Connecting...</div>
                    </div>
                  </div>
                ) : null}
                
                {/* Offline overlay or fallback */}
                <div 
                  className={`absolute inset-0 flex items-center justify-center ${
                    isOnline ? 'hidden' : 'bg-black'
                  }`}
                  style={{ display: isOnline ? 'none' : 'flex' }}
                >
                  <div className="text-center text-white">
                    <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-700 flex items-center justify-center">
                      <span className="text-lg">💻</span>
                    </div>
                    <div className="text-xs">Offline</div>
                  </div>
                </div>

                {/* Online indicator */}
                {isOnline && (
                  <div className="absolute top-2 right-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>

              {/* Student Info */}
              <div className="p-3 bg-white rounded-b-lg">
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold transition-transform duration-300 ease-in-out hover:scale-[3] hover:z-50 relative cursor-pointer ${getAvatarColor(studentName)}`}>
                    {studentName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {studentName}
                    </div>
                    <div className="text-xs text-gray-500">
                      ID: {student.school_id || student.person_id}
                    </div>
                  </div>
                </div>
                
                {isOnline && connectedData && (
                  <>
                    <div className="mt-2 text-xs text-green-600">
                      IP: {connectedData.ip}
                    </div>
                    <div className="mt-2 flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          blankScreen(student);
                        }}
                        className="flex-1 px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                        title="Blank screen"
                      >
                        ⚫
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          unblankScreen(student);
                        }}
                        className="flex-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                        title="Unblank screen"
                      >
                        ✅
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const content = prompt('Enter content to push to ' + studentName + ':');
                          if (content) pushContentToStudent(student, content);
                        }}
                        className="flex-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                        title="Push content"
                      >
                        📤
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openFullscreenRemoteControl(student);
                        }}
                        className="flex-1 px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors"
                        title="Take remote control"
                      >
                        🎮
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* No Students Message */}
      {students.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">No students found for Form {formId}</div>
        </div>
      )}

      {/* Fullscreen Remote Control Modal */}
      {fullscreenStudent && (
        <div className="fixed inset-0 bg-black z-50">
          <div className="relative w-full h-full">
            {/* Header Bar */}
            <div className="absolute top-0 left-0 right-0 bg-gray-900 bg-opacity-90 text-white p-4 z-10 flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                🎮 {fullscreenStudent.full_name || fullscreenStudent.name} - Remote Control
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const connectedData = getConnectedStudentData(fullscreenStudent);
                    if (connectedData) {
                      // Send enable remote control command via WebSocket
                      const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
                      const ws = remoteControlSessions[`fullscreen_${studentId}`];
                      
                      if (ws && ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ command: 'enable_remote_control' }));
                        setControlAction(`🎮 Taking remote control of ${fullscreenStudent.full_name || fullscreenStudent.name}'s laptop`);
                        setTimeout(() => setControlAction(''), 3000);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors font-medium"
                >
                  🎮 Take Control
                </button>
                <button
                  onClick={() => {
                    // Switch to HD mode if not already
                    const connectedData = getConnectedStudentData(fullscreenStudent);
                    if (connectedData) {
                      const studentId = fullscreenStudent.school_id || fullscreenStudent.person_id;
                      const ws = remoteControlSessions[`fullscreen_${studentId}`];
                      
                      if (ws && ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ command: 'start_hd_stream' }));
                        setControlAction(`📺 Switching to HD mode for ${fullscreenStudent.full_name || fullscreenStudent.name}`);
                        setTimeout(() => setControlAction(''), 3000);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-medium"
                >
                  📺 HD Mode
                </button>
                <button
                  onClick={closeFullscreenRemoteControl}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Screen Canvas */}
            <canvas
              ref={canvasRef}
              className="w-full h-full cursor-crosshair"
              onClick={handleCanvasClick}
              onMouseMove={handleCanvasMouseMove}
              onKeyDown={handleCanvasKeyDown}
              tabIndex={0}
            />
          </div>
        </div>
      )}

      {/* Legacy Fullscreen Modal (keep for compatibility) */}
      {selectedStudent && !fullscreenStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-6xl max-h-full p-4">
            <button
              onClick={() => setSelectedStudent(null)}
              className="absolute top-2 right-2 text-white hover:text-gray-300 text-2xl z-10"
            >
              ✕
            </button>
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="p-4 bg-gray-100 border-b">
                <h3 className="text-lg font-semibold">
                  {selectedStudent.full_name || selectedStudent.name} - Live Screen
                </h3>
              </div>
              <div className="relative">
                {(() => {
                  const connectedData = getConnectedStudentData(selectedStudent);
                  return connectedData ? (
                    <div className="w-96 h-64 bg-gray-200 flex items-center justify-center">
                      <button
                        onClick={() => openFullscreenRemoteControl(selectedStudent)}
                        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        🎮 Launch Remote Control
                      </button>
                    </div>
                  ) : (
                    <div className="w-96 h-64 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500">Student not connected</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push Content Modal */}
      {showPushModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">📤 Push Content to All Students</h3>
            <textarea
              value={pushContent}
              onChange={(e) => setPushContent(e.target.value)}
              placeholder="Enter content to display on all student screens..."
              className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={pushToAllStudents}
                disabled={!pushContent.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Push to All
              </button>
              <button
                onClick={() => {
                  setShowPushModal(false);
                  setPushContent('');
                }}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { HomeTab };
export default FormPage;
